/**
 * useCamera — CameraModal component
 *
 * Adaptive camera modal that re-layouts for portrait and landscape.
 *
 * Portrait layout:
 *   ┌──────────────┐
 *   │ title ··· ⚡⟳✕ │
 *   │               │
 *   │  video feed   │
 *   │               │
 *   │  zoom slider  │
 *   │  controls bar │
 *   └──────────────┘
 *
 * Landscape layout:
 *   ┌──────────────────────────────┐
 *   │              │  close  title │
 *   │  video feed  │  flash  focus │
 *   │              │  switch  zoom │
 *   │              │ [  shutter  ] │
 *   └──────────────────────────────┘
 */

import { type CSSProperties, useState, useRef, useCallback, useEffect, useMemo } from 'react';
import Image from 'next/image';
import { createPortal } from 'react-dom';
import { EMBEDDED_FAVICON } from '@/lib/embeddedAssets';
import { useModalStateSafe } from '@/contexts/ModalStateContext';
import { useDocumentEdgeDetection, type DocumentEdge } from '@/hooks/useDocumentEdgeDetection';
import { useDocumentEdgeDetectionML, type DocumentEdgeML } from '@/hooks/useDocumentEdgeDetectionML';
import { useDocumentReadability, type DocumentReadabilityReason } from '@/hooks/useDocumentReadability';
import { correctPerspectiveFromBase64, refineCornersFromVideo, type Point } from '@/lib/perspectiveTransform';
import { haptics } from '@/lib/haptics';

import type {
  CameraModalProps,
  CaptureShortcut,
  CaptureModeButton,
  CropRect,
} from './types';
import { useOrientation } from './useOrientation';
import { CameraActionButton } from './CameraActionButton';
import { getCameraGuideFrameStyle } from './cameraGuideUtils';
import { focusRingKeyframes, AUTO_CAPTURE_PREF_KEY, AUTO_TORCH_PREF_KEY, AUTO_ZOOM_PREF_KEY } from './constants';
import {
  IconClose,
  IconSwitchCamera,
  TorchIcon,
  IconFocus,
  IconZoomIn,
  IconZoomOut,
  IconCamera,
} from './icons';
import { VehicleSilhouette } from './vehicleSilhouettes';

export function CameraModal({
  camera,
  title = 'Take Photo',
  guide,
  cropToGuide,
  captureButton,
  onAfterCapture,
  autoCapture: autoCaptureEnabled = false,
  autoCaptureConfidence = 0.5,
  autoCaptureStabilityMs = 1000,
  autoCaptureCountdownSec = 3,
  reviewCapture = false,
  batchCapture = false,
  batchMaxPhotos = 10,
  shortcuts = [],
  captureModeGrid,
  hardwareCapture = true,
  perspectiveCorrection = false,
  darkModeEnhance = false,
  reviewButtons = 'default',
  autoZoom: autoZoomEnabled = false,
  autoZoomTargetFill = 0.75,
  autoZoomConfidence = 0.3,
  contextPhotos = [],
  captureMode,
  onBatchComplete,
  expenseContext,
  documentProfile = 'auto',
  vehicleProfile = 'front',
  preferredOrientation,
  edgeDetection = true,
  requireReadableDocument = false,
}: CameraModalProps) {
  const {
    isOpen,
    close,
    attachVideoElement,
    videoRef,
    stream,
    error,
    facingMode,
    switchCamera,
    canSwitchCamera,
    availableCameraCount,
    capture,
    isLoading,
    startCamera,
    torchEnabled,
    torchSupported,
    isTorchChecking,
    toggleTorch,
    setTorch,
    ambientBrightness,
    zoomLevel,
    zoomMin,
    zoomMax,
    zoomSupported,
    isDigitalZoom,
    setZoom,
    tapToFocus,
    autofocusActive,
    toggleFocusMode,
    submitPhoto,
  } = camera;

  const isLandscape = useOrientation();
  const modalZIndexClassName = 'z-[300]';

  // Handle hardware back button via ModalStateContext (avoids double-firing with global handler)
  // useModalStateSafe returns null when outside ModalStateProvider
  const modalState = useModalStateSafe();

  // Track if we've started the camera for this open session
  const hasStartedRef = useRef(false);

  // Refs for guide-frame cropping
  const videoContainerRef = useRef<HTMLDivElement>(null);
  const guideFrameRef = useRef<HTMLDivElement>(null);

  // Dynamic document edge detection — only active in 'auto' and 'vehicle' modes.
  // Samples the live video feed and detects the receipt/paper boundary
  // using brightness-based image processing (no ML model required).
  // This mirrors how Dext draws a dynamic bounding box around receipts.
  // For vehicle mode, the edge detection is a guidance tool — it detects the
  // vehicle body as a large object in the frame and draws a bounding box to
  // help the user frame the photo. It is NOT enforced (capture is not blocked).
  const isAutoMode = !guide || guide.mode === 'auto';
  const isVehicleMode = guide?.mode === 'vehicle';
  const edgeDetectionEnabled = isOpen && (isAutoMode || isVehicleMode) && edgeDetection;

  // ML-based document edge detection (DocAligner TFLite model).
  // Hybrid architecture: ML runs at a lower frequency (500ms) and provides
  // 4 actual corners + sharpness for perspective correction at capture time.
  // The classical canvas-based detector below drives real-time edge tracking
  // and auto-capture — it's faster and more tolerant of partial documents.
  // Disabled in vehicle mode: DocAligner is a document-corner model, so on a
  // vehicle scene it false-positives on background rectangles (signs, windows,
  // number plates) — the bounding box teleports to a corner of the frame and
  // back. Vehicle framing uses the classical detector only.
  const mlEdge = useDocumentEdgeDetectionML(videoRef, {
    enabled: edgeDetectionEnabled && !isVehicleMode,
    intervalMs: 500,
    smoothing: 0.4,
    heatmapThreshold: 0.3,
    minConfidence: 0.3,
  });

  // Canvas-based edge detection — always runs, drives auto-capture and display.
  // This is the primary detector: fast, tolerant of partial documents, and
  // produces stable bounding boxes for the auto-capture stability check.
  // For vehicle mode, uses a wider sample and lower contrast thresholds to
  // detect the vehicle body (which is not necessarily brighter than the
  // background, unlike white paper receipts).
  const { documentEdge: canvasEdge } = useDocumentEdgeDetection(videoRef, {
    enabled: edgeDetectionEnabled,
    // Throttled to ~5fps (200ms) instead of ~8fps (120ms) to reduce CPU
    // load on mobile devices. The bounding box still tracks smoothly
    // because the temporal EMA smoothing (0.5) bridges the gap between
    // samples. This was the primary source of perceived camera slowness
    // during receipt capture — each frame ran canvas drawImage + getImageData
    // + Otsu thresholding + gradient analysis.
    intervalMs: 200,
    sampleWidth: 80,
    smoothing: 0.5,
  });

  // Hybrid: classical detector is primary for display + auto-capture
  // fallback. ML corners + confidence drive auto-capture when available
  // (more accurate, detects actual documents including long slips that
  // extend beyond the frame).
  const documentEdge: DocumentEdge | null = canvasEdge;

  // The ML edge with corners/sharpness, when available. Used by
  // perspective correction, quality display, and auto-capture gating.
  const mlEdgeWithCorners: DocumentEdgeML | null = mlEdge.isInitialized
    ? mlEdge.documentEdge
    : null;

  // Edge used for the on-screen bounding box. Prefer the ML edge when
  // available — its bounds come from actual detected corners, whereas the
  // canvas detector inflates its box by ~10% of the frame per side to
  // compensate for inward gradient bias. That padding is harmless for
  // cropping, but it makes the live preview box render noticeably larger
  // than the document.
  const displayEdge: DocumentEdge | null = mlEdgeWithCorners ?? documentEdge;

  // Readability assessment — measures text-scale detail inside the detected
  // document region (stroke density + region sharpness + frame fill). A slip
  // detected a metre away produces almost no resolvable detail, so OCR on
  // the captured photo would return nothing. Only assessed for document
  // modes — vehicle framing doesn't need OCR readability.
  const docReadability = useDocumentReadability(videoRef, displayEdge, {
    enabled: edgeDetectionEnabled && isAutoMode,
  });

  // Ref mirror of documentEdge so runZoomCycle/zoomOutToFind can check the
  // latest edge without having documentEdge in their dependency arrays (which
  // would recreate the callbacks every 200ms and restart animations).
  const documentEdgeRef = useRef(documentEdge);
  documentEdgeRef.current = documentEdge;

  // Ref mirror of ML edge (with corners) for use in handleCapture without
  // adding it to the dependency array (which would recreate on every frame).
  const mlEdgeRef = useRef(mlEdgeWithCorners);
  mlEdgeRef.current = mlEdgeWithCorners;

  // Ref mirror of the readability verdict for use in handleCapture and the
  // auto-capture effect without adding it to dependency arrays.
  const readabilityRef = useRef(docReadability);
  readabilityRef.current = docReadability;

  // Ref mirror of zoomLevel so smoothZoomTo and the controller effect can read
  // the latest zoom without having zoomLevel in their dependency arrays (which
  // would cancel and restart animations on every animation frame).
  const zoomLevelRef = useRef(zoomLevel);
  zoomLevelRef.current = zoomLevel;

  // Ref mirror of isDigitalZoom so the controller effect can branch on zoom
  // type without having isDigitalZoom in its dependency array.
  const isDigitalZoomRef = useRef(isDigitalZoom);
  isDigitalZoomRef.current = isDigitalZoom;

  // Compute crop rect (relative 0–1) from the guide frame's position on screen
  const computeCropRectFromGuide = useCallback((): CropRect | undefined => {
    const container = videoContainerRef.current;
    const frame = guideFrameRef.current;
    const video = videoRef.current;
    if (!container || !frame || !video || !video.videoWidth || !video.videoHeight) return undefined;

    const containerRect = container.getBoundingClientRect();
    const frameRect = frame.getBoundingClientRect();

    // Position of the guide frame relative to the video container
    const relLeft = frameRect.left - containerRect.left;
    const relTop = frameRect.top - containerRect.top;
    const frameW = frameRect.width;
    const frameH = frameRect.height;

    // object-cover: video fills container, overflow cropped, centered
    const vw = video.videoWidth;
    const vh = video.videoHeight;
    const scale = Math.max(containerRect.width / vw, containerRect.height / vh);
    const displayW = vw * scale;
    const displayH = vh * scale;
    const offsetX = (containerRect.width - displayW) / 2;
    const offsetY = (containerRect.height - displayH) / 2;

    // Map display coords → video pixel coords
    const cropX = (relLeft - offsetX) / scale;
    const cropY = (relTop - offsetY) / scale;
    const cropW = frameW / scale;
    const cropH = frameH / scale;

    // 5% padding so we don't cut off receipt edges, clamped to [0, 1]
    const padX = cropW * 0.05;
    const padY = cropH * 0.05;

    return {
      x: Math.max(0, (cropX - padX) / vw),
      y: Math.max(0, (cropY - padY) / vh),
      width: Math.min(1, (cropW + 2 * padX) / vw),
      height: Math.min(1, (cropH + 2 * padY) / vh),
    };
  }, [videoRef]);

  // ---- Capture Review state (declared early so handleCapture can use it) ----
  const [reviewImage, setReviewImage] = useState<string | null>(null);
  // Metadata about the document at capture time — used by the smart review
  // screen to decide whether to offer "Capture Another" (multi-page) or not.
  // - allCornersDetected: all 4 corners confirmed in frame (complete document),
  //   via confident ML corners OR classical edge transitions on all sides
  // - documentType: 'till-slip' (long, narrow) vs 'a4' (roughly A4 ratio) vs 'unknown'
  // - cornerCount: how many corners were confirmed in frame (0–4)
  const [reviewCaptureMeta, setReviewCaptureMeta] = useState<{
    allCornersDetected: boolean;
    documentType: 'till-slip' | 'a4' | 'unknown';
    cornerCount: number;
  }>({ allCornersDetected: false, documentType: 'unknown', cornerCount: 0 });

  // Whether multi-page capture is possible for the current photo.
  // True when:
  //   - batchCapture is enabled (the flow supports adding pages), AND
  //   - the document might have more content:
  //     - Partial detection (not all corners detected) — the document
  //       extends beyond the frame, so there may be more to capture.
  //     - No corners detected at all — we can't tell, so assume possible.
  //     - A4 profile — multi-page invoices are common.
  // False when:
  //   - All 4 corners detected AND documentProfile is 'till-slip' — a
  //     complete till slip in frame is definitely single-page.
  //   - batchCapture is false — the flow doesn't support adding pages.
  const multiPagePossible = batchCapture && (
    !reviewCaptureMeta.allCornersDetected ||
    documentProfile !== 'till-slip'
  );

  // ---- Perspective Correction state ----
  const [perspectiveCorners, setPerspectiveCorners] = useState<Point[] | null>(null);
  const [perspectiveApplied, setPerspectiveApplied] = useState(false);
  const [perspectiveApplying, setPerspectiveApplying] = useState(false);
  const [perspectiveOriginal, setPerspectiveOriginal] = useState<string | null>(null);
  const reviewImageRef = useRef<HTMLImageElement | null>(null);
  const reviewContainerRef = useRef<HTMLDivElement | null>(null);
  const [reviewImgLayout, setReviewImgLayout] = useState<{ left: number; top: number; width: number; height: number } | null>(null);
  const reviewActiveRef = useRef(false);
  useEffect(() => { reviewActiveRef.current = !!reviewImage; }, [reviewImage]);
  const draggingCornerRef = useRef<number | null>(null);
  const restoreTorchOnReviewDismissRef = useRef(false);

  // ---- Dark Mode Enhancement state ----
  // ambientBrightness comes from the useCamera hook for low-light detection.
  // We still need isLowLight (derived) and the post-capture enhancer.
  // Aligned with the auto-torch HYSTERESIS_LOW (100) so the low-light banner
  // and post-capture enhancement trigger at the same point the torch would.
  const isLowLight = ambientBrightness !== null && ambientBrightness < 100;

  // Post-capture brightness enhancement for dark conditions
  const enhanceDarkCapture = useCallback((base64Image: string): Promise<string> => {
    return new Promise((resolve) => {
      const img = document.createElement('img');
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = img.naturalWidth;
          canvas.height = img.naturalHeight;
          const ctx = canvas.getContext('2d')!;
          ctx.drawImage(img, 0, 0);

          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const data = imageData.data;

          // Compute current average luminance
          let sum = 0;
          const pixelCount = data.length / 4;
          for (let i = 0; i < data.length; i += 4) {
            sum += (data[i] * 299 + data[i + 1] * 587 + data[i + 2] * 114) / 1000;
          }
          const avgLum = sum / pixelCount;

          // Only enhance if the image is genuinely dark (< 80 avg luminance)
          if (avgLum >= 80) {
            resolve(base64Image);
            return;
          }

          // Compute a brightness boost factor and mild contrast stretch
          // Target ~120 average luminance (well-lit document)
          const targetLum = 120;
          const boost = Math.min(2.5, targetLum / Math.max(avgLum, 20));

          // Simple per-channel adjustment: scale around 128 (contrast) + shift (brightness)
          for (let i = 0; i < data.length; i += 4) {
            for (let c = 0; c < 3; c++) {
              const idx = i + c;
              // Stretch contrast around midpoint, then apply brightness boost
              const stretched = (data[idx] - 128) * 1.15 + 128;
              const boosted = stretched * boost;
              data[idx] = Math.max(0, Math.min(255, boosted));
            }
          }

          ctx.putImageData(imageData, 0, 0);
          resolve(canvas.toDataURL('image/jpeg', 0.92));
        } catch {
          resolve(base64Image);
        }
      };
      img.onerror = () => resolve(base64Image);
      img.src = base64Image;
    });
  }, []);

  // ---- Batch Capture state ----
  const [batchPhotos, setBatchPhotos] = useState<string[]>([]);

  // ---- Capture Mode state ----
  // User-selectable mode (defaults from prop). When captureMode is provided,
  // the selector is shown; otherwise the behavior falls back to batchCapture prop.
  const effectiveInitialMode: 'single' | 'combine' | 'multiple' =
    captureMode || (batchCapture ? 'combine' : 'single');
  const [captureModeState, setCaptureModeState] = useState<'single' | 'combine' | 'multiple'>(effectiveInitialMode);
  const isBatchMode = captureModeState === 'combine' || captureModeState === 'multiple';
  const showModeSelector = !!captureMode;

  // ---- Auto-Capture state (declared early so handleCapture/handleRetake can use it) ----
  const AUTO_CAPTURE_PREF_KEY = 'camera_auto_capture_enabled';
  const [autoCaptureOn, setAutoCaptureOn] = useState(() => {
    try {
      const stored = localStorage.getItem(AUTO_CAPTURE_PREF_KEY);
      return stored === null ? autoCaptureEnabled : stored === 'true';
    } catch { return autoCaptureEnabled; }
  });
  const [countdown, setCountdown] = useState<number | null>(null);
  const edgeHistoryRef = useRef<Array<{ x: number; y: number; w: number; h: number; c: number; t: number }>>([]);
  const stableSinceRef = useRef<number | null>(null);
  const countdownTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const autoCaptureTriggeredRef = useRef(false);

  // ---- Readability gate state ----
  // When requireReadableDocument is on and a document is detected but its
  // text isn't resolvable (too far away / blurry / no detail), the first
  // capture tap is blocked and this reason drives the "tap again to capture
  // anyway" hint. forceCaptureUntilRef holds the 4s override window so a
  // mis-detection can never lock the user out of the shutter.
  const [captureGateReason, setCaptureGateReason] = useState<DocumentReadabilityReason | null>(null);
  const forceCaptureUntilRef = useRef(0);

  // ---- Torch mode state (M2) ----
  // 3-state torch control: 'off' (torch forced off), 'on' (torch forced on),
  // 'auto' (torch controlled by ambient brightness hysteresis).
  // The lightning bolt icon reflects the mode:
  //  - off:  white outline bolt
  //  - auto: amber outline bolt
  //  - on:   amber filled bolt
  const AUTO_TORCH_PREF_KEY = 'camera_torch_mode';
  type TorchMode = 'off' | 'on' | 'auto';
  const [torchMode, setTorchMode] = useState<TorchMode>('off');
  const torchModeRef = useRef<TorchMode>(torchMode);
  torchModeRef.current = torchMode;
  const autoTorchPendingRef = useRef(false);
  // Pause automatic torch management while the user is reviewing a captured
  // photo, so the torch can be turned off during review without the auto
  // effects immediately turning it back on.
  const [reviewPaused, setReviewPausedState] = useState(false);
  const isReviewPausedRef = useRef(false);
  const setReviewPaused = useCallback((paused: boolean) => {
    isReviewPausedRef.current = paused;
    setReviewPausedState(paused);
  }, []);
  // Debounce timer for turning torch OFF in auto mode — prevents oscillation
  // when a white document reflects torch light back, causing brightness to
  // spike above the HIGH threshold momentarily.
  const autoTorchOffTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ---- Auto-Zoom state (M3) ----
  const AUTO_ZOOM_PREF_KEY = 'camera_auto_zoom_enabled';
  const [autoZoomOn, setAutoZoomOn] = useState(() => {
    try {
      const stored = localStorage.getItem(AUTO_ZOOM_PREF_KEY);
      return stored === null ? (autoZoomEnabled || false) : stored === 'true';
    } catch { return false; }
  });
  const autoZoomRafRef = useRef<number | null>(null);
  const noEdgeSinceRef = useRef<number | null>(null);
  const settledSinceRef = useRef<number | null>(null);

  // Auto-capture and auto-zoom can coexist. When both are enabled, the
  // auto-capture effect gates on the auto-zoom being "settled" (no active
  // zoom animation and the zoom level is within tolerance of the target fill
  // ratio). See the auto-capture effect below for the gating logic.
  const effectiveAutoCapture = autoCaptureOn;

  // Handle hardware back button via ModalStateContext
  useEffect(() => {
    if (!isOpen || !modalState?.registerBackHandler) return;

    const handleBack = () => {
      if (reviewActiveRef.current) {
        console.log('[CameraModal] Back button — cancelling review, returning to live camera');
        setReviewImage(null);
        setReviewPaused(false);
        if (restoreTorchOnReviewDismissRef.current) {
          restoreTorchOnReviewDismissRef.current = false;
          void setTorch(true);
        }
        autoCaptureTriggeredRef.current = false;
        setPerspectiveCorners(null);
        setPerspectiveApplied(false);
        setPerspectiveOriginal(null);
        return true;
      }
      console.log('[CameraModal] Back button handled via ModalState - closing camera');
      close();
      return true;
    };

    modalState.registerBackHandler(handleBack);
    return () => {
      modalState.unregisterBackHandler(handleBack);
    };
  }, [isOpen, close, modalState, setReviewPaused, setTorch]);

  const handleCapture = useCallback(async () => {
    if (isBatchMode && batchPhotos.length >= batchMaxPhotos) return;

    // Readability gate — a detected document whose text isn't resolvable
    // (slip held too far away, blurry, or a featureless bright region) would
    // produce a photo OCR can't read. The first tap warns; a second tap
    // within 4s forces the capture so a mis-detection can't lock the user
    // out. When no document is detected at all, capture stays allowed.
    const readabilityNow = readabilityRef.current;
    if (requireReadableDocument && isAutoMode && readabilityNow && !readabilityNow.readable) {
      const now = Date.now();
      if (forceCaptureUntilRef.current > now) {
        forceCaptureUntilRef.current = 0;
      } else {
        forceCaptureUntilRef.current = now + 4000;
        setCaptureGateReason(readabilityNow.reason ?? 'low-detail');
        void haptics.warning();
        return;
      }
    }
    setCaptureGateReason(null);
    void haptics.heavy();

    // Compute crop rect — prefer document edge detection when available
    // for accurate cropping to the detected receipt boundary.
    let cropRect: CropRect | undefined = cropToGuide ? computeCropRectFromGuide() : undefined;

    if (documentEdge && documentEdge.confidence > 0.3) {
      // Use the detected document boundary directly (in full-frame 0–1 coords)
      // with 5% padding so receipt edges aren't cut off
      const padX = documentEdge.width * 0.05;
      const padY = documentEdge.height * 0.05;
      cropRect = {
        x: Math.max(0, documentEdge.x - padX),
        y: Math.max(0, documentEdge.y - padY),
        width: Math.min(1, documentEdge.width + 2 * padX),
        height: Math.min(1, documentEdge.height + 2 * padY),
      };

      // When digital zoom is active, capture() interprets cropRect as
      // relative to the zoomed (center-cropped) region, not the full frame.
      // Convert from full-frame coords to zoomed-region coords.
      if (isDigitalZoom && zoomLevel > 1) {
        const zoomedW = 1 / zoomLevel;
        const zoomedH = 1 / zoomLevel;
        const zoomedX = (1 - zoomedW) / 2;
        const zoomedY = (1 - zoomedH) / 2;
        cropRect = {
          x: Math.max(0, Math.min(1, (cropRect.x - zoomedX) / zoomedW)),
          y: Math.max(0, Math.min(1, (cropRect.y - zoomedY) / zoomedH)),
          width: Math.max(0, Math.min(1, cropRect.width / zoomedW)),
          height: Math.max(0, Math.min(1, cropRect.height / zoomedH)),
        };
      }
    }

    // Effective captured region in full-frame video coordinates (0–1) —
    // used below to remap detected corners into captured-image space for
    // the perspective overlay. When digital zoom is active, cropRect is
    // relative to the center-cropped zoomed region (a guide-frame cropRect
    // is already in that same screen-relative space), so map it back to
    // the full frame. Mapping the clamped zoomed-space rect back yields
    // exactly the region capture() draws.
    let capturedRegion = cropRect;
    if (cropRect && isDigitalZoom && zoomLevel > 1) {
      const zoomedW = 1 / zoomLevel;
      const zoomedH = 1 / zoomLevel;
      capturedRegion = {
        x: (1 - zoomedW) / 2 + cropRect.x * zoomedW,
        y: (1 - zoomedH) / 2 + cropRect.y * zoomedH,
        width: cropRect.width * zoomedW,
        height: cropRect.height * zoomedH,
      };
    }

    let result = await capture(cropRect);
    if (result) {
      // Apply dark mode post-capture enhancement if enabled and conditions are dark
      if (darkModeEnhance && isLowLight) {
        result = await enhanceDarkCapture(result);
      }
      if (reviewCapture) {
        // Pause automatic torch management and turn the torch off before
        // showing the review overlay, so the user isn't reviewing a photo
        // with the torch shining in their face or wasting battery.
        restoreTorchOnReviewDismissRef.current = torchEnabled;
        setReviewPaused(true);
        await setTorch(false);
        setReviewImage(result);

        // Capture document metadata for the smart review screen.
        // The ML model detects actual document corners — if all 4 are
        // visible, the document is fully in frame. If fewer than 4, the
        // document extends beyond the frame (e.g. a long till slip where
        // only the top portion is captured).
        // The ML model always returns 4 corners (one per heatmap channel),
        // so we check per-corner confidence AND whether corners are near
        // the frame edge to detect partial documents.
        const mlCornersAtCapture = mlEdgeRef.current?.corners;
        const mlCornerConfidences = mlEdgeRef.current?.cornerConfidences;
        // A corner is "confident" if its heatmap peak is above 0.5
        // (the overall threshold is 0.3, but individual corners at 0.3-0.5
        // are weak — likely predicted from context, not actually visible).
        const CORNER_CONFIDENCE_THRESHOLD = 0.5;
        // A corner is "at frame edge" if it's within 5% of the frame border,
        // meaning the document extends beyond the visible area.
        const EDGE_MARGIN = 0.05;
        const mlCornerVisible = (i: number) => {
          const c = mlCornersAtCapture?.[i];
          const conf = mlCornerConfidences?.[i] ?? 0;
          return !!c && conf >= CORNER_CONFIDENCE_THRESHOLD &&
            c.x >= EDGE_MARGIN && c.x <= 1 - EDGE_MARGIN &&
            c.y >= EDGE_MARGIN && c.y <= 1 - EDGE_MARGIN;
        };
        // A corner also counts when the classical detector confirmed real
        // edge transitions on both of its adjacent sides inside the frame —
        // DocAligner under-confides on curled or low-contrast slip bottoms,
        // so ML alone under-counts fully-visible documents. On partial
        // captures (top/middle segments) the classical row bounds fall back
        // to full frame height, so those sides stay false and the count
        // remains conservative.
        const s = documentEdge?.sides;
        const cornerVisible = [
          mlCornerVisible(0) || !!(s?.top && s?.left),
          mlCornerVisible(1) || !!(s?.top && s?.right),
          mlCornerVisible(2) || !!(s?.bottom && s?.right),
          mlCornerVisible(3) || !!(s?.bottom && s?.left),
        ];
        const cornerCount = cornerVisible.filter(Boolean).length;
        const allCornersDetected = cornerCount === 4;
        // Classify document type by aspect ratio of the detected edge.
        // Till slips are tall and narrow (aspectRatio < 0.55, i.e. height > width).
        // A4 documents are closer to 0.707 ratio (210mm x 297mm).
        // Use the ML edge if available, otherwise the classical edge.
        const metaEdge = mlEdgeRef.current ?? documentEdge;
        const aspect = metaEdge?.aspectRatio ?? 0;
        const documentType: 'till-slip' | 'a4' | 'unknown' =
          aspect > 0 && aspect < 0.55 ? 'till-slip' :
          aspect >= 0.55 && aspect <= 0.85 ? 'a4' :
          'unknown';
        setReviewCaptureMeta({ allCornersDetected, documentType, cornerCount });
        // Initialize perspective correction corners from document edge detection
        if (perspectiveCorrection && documentEdge) {
          const video = videoRef.current;
          // Prefer ML-detected corners (4 actual corners) when available.
          // Fall back to canvas-based refineCornersFromVideo (which refines
          // a bounding box into corners via gradient search) when ML is
          // not active or hasn't detected a document.
          const mlCorners = mlEdgeRef.current?.corners;
          const mlConfs = mlEdgeRef.current?.cornerConfidences;
          let corners: Point[];
          if (mlCorners && mlCorners.length === 4) {
            // ML corners are already in relative coordinates (0–1) and
            // ordered clockwise from top-left, exactly what
            // correctPerspectiveFromBase64 expects.
            corners = mlCorners;
            // Weak peaks (<0.5) are predicted positions, not observed
            // corners — they regularly land off the slip on curled or
            // low-contrast edges, which misaligns the review overlay.
            // Substitute per-axis from gradient-refined corners of the
            // classical box, but only on axes where the classical
            // detector actually found that side's boundary — otherwise
            // the box edge is a full-extent fallback and refinement
            // would snap to frame-edge noise.
            const s = documentEdge?.sides;
            if (s && (s.top || s.bottom || s.left || s.right) && video && video.videoWidth) {
              const refined = refineCornersFromVideo(video, {
                x: documentEdge.x,
                y: documentEdge.y,
                width: documentEdge.width,
                height: documentEdge.height,
              });
              corners = mlCorners.map((c, i) => {
                if ((mlConfs?.[i] ?? 0) >= CORNER_CONFIDENCE_THRESHOLD) return c;
                const r = refined[i];
                // corner order: TL(0), TR(1), BR(2), BL(3)
                const useRefX = (i === 0 || i === 3) ? !!s.left : !!s.right;
                const useRefY = i < 2 ? !!s.top : !!s.bottom;
                return { x: useRefX ? r.x : c.x, y: useRefY ? r.y : c.y };
              });
            }
          } else if (video && video.videoWidth) {
            corners = refineCornersFromVideo(video, {
              x: documentEdge.x,
              y: documentEdge.y,
              width: documentEdge.width,
              height: documentEdge.height,
            });
          } else {
            corners = [
              { x: 0.1, y: 0.1 },
              { x: 0.9, y: 0.1 },
              { x: 0.9, y: 0.9 },
              { x: 0.1, y: 0.9 },
            ];
          }
          // Remap corners from full-video coordinate space (0–1 of video
          // dimensions) to captured-image coordinate space (0–1 of the
          // captured image) using the effective captured region.  Without
          // this remapping the perspective overlay on the review screen is
          // misaligned because the captured image is a cropped subset of
          // the full video frame.
          if (capturedRegion) {
            setPerspectiveCorners(corners.map(c => ({
              x: Math.max(0, Math.min(1, (c.x - capturedRegion.x) / capturedRegion.width)),
              y: Math.max(0, Math.min(1, (c.y - capturedRegion.y) / capturedRegion.height)),
            })));
          } else {
            setPerspectiveCorners(corners);
          }
        } else if (perspectiveCorrection) {
          // No edge detected — use default margins
          setPerspectiveCorners([
            { x: 0.1, y: 0.1 },
            { x: 0.9, y: 0.1 },
            { x: 0.9, y: 0.9 },
            { x: 0.1, y: 0.9 },
          ]);
        }
        setPerspectiveApplied(false);
        setPerspectiveOriginal(null);
      } else if (isBatchMode) {
        setBatchPhotos(prev => [...prev, result as string]);
        autoCaptureTriggeredRef.current = false;
      } else {
        onAfterCapture?.();
        close();
      }
    }
  }, [capture, cropToGuide, computeCropRectFromGuide, onAfterCapture, close, reviewCapture, isBatchMode, batchPhotos.length, batchMaxPhotos, perspectiveCorrection, documentEdge, videoRef, darkModeEnhance, isLowLight, enhanceDarkCapture, isDigitalZoom, zoomLevel, torchEnabled, setTorch, setReviewPaused, requireReadableDocument, isAutoMode]);

  // Ref so auto-capture countdown can call the latest handleCapture
  const handleCaptureRef = useRef(handleCapture);
  useEffect(() => {
    handleCaptureRef.current = handleCapture;
  }, [handleCapture]);

  // Refs so handleTorchToggle can read the latest torch state and call the
  // latest toggleTorch without having them in its dependency array.
  // toggleTorch depends on zoomLevel (which changes every frame during
  // auto-zoom), so including it in deps would recreate the callback constantly.
  const toggleTorchRef = useRef(toggleTorch);
  useEffect(() => {
    toggleTorchRef.current = toggleTorch;
  }, [toggleTorch]);
  const torchEnabledRef = useRef(torchEnabled);
  useEffect(() => {
    torchEnabledRef.current = torchEnabled;
  }, [torchEnabled]);

  // ---- Torch mode toggle: cycle off → auto → on → off (M2) ----
  // For 'on' and 'off', the torch is toggled immediately here. For 'auto',
  // the hysteresis effect below controls the torch based on ambient brightness.
  const handleTorchToggle = useCallback(async () => {
    const prev = torchModeRef.current;
    const next: TorchMode = prev === 'off' ? 'auto' : prev === 'auto' ? 'on' : 'off';
    setTorchMode(next);
    void haptics.toggle();
    torchModeRef.current = next;
    try { localStorage.setItem(AUTO_TORCH_PREF_KEY, next); } catch { /* ignore */ }

    // For 'on': turn torch on if it's off. For 'off': turn torch off if it's on.
    // For 'auto': let the hysteresis effect handle it.
    if ((next === 'on' && !torchEnabledRef.current) || (next === 'off' && torchEnabledRef.current)) {
      autoTorchPendingRef.current = true;
      void toggleTorchRef.current().finally(() => { autoTorchPendingRef.current = false; });
    }
  }, [AUTO_TORCH_PREF_KEY]);

  // Capture mode grid handler — calls button onClick then triggers capture
  const handleModeCapture = useCallback((btn: CaptureModeButton) => {
    btn.onClick();
    handleCapture();
  }, [handleCapture]);

  // Reset review image when camera closes
  useEffect(() => {
    if (!isOpen) {
      setReviewImage(null);
      setBatchPhotos([]);
      setPerspectiveCorners(null);
      setPerspectiveApplied(false);
      setPerspectiveApplying(false);
      setPerspectiveOriginal(null);
      setReviewPaused(false);
    }
  }, [isOpen, setReviewPaused]);

  const handleUsePhoto = useCallback(() => {
    setReviewImage(null);
    setReviewPaused(false);
    if (isBatchMode) {
      setBatchPhotos(prev => [...prev, reviewImage!]);
      autoCaptureTriggeredRef.current = false;
      if (restoreTorchOnReviewDismissRef.current) {
        restoreTorchOnReviewDismissRef.current = false;
        void setTorch(true);
      }
    } else {
      submitPhoto(reviewImage!);
      onAfterCapture?.();
      close();
    }
    void haptics.success();
  }, [onAfterCapture, close, isBatchMode, reviewImage, submitPhoto, setReviewPaused, setTorch]);

  const handleRetake = useCallback(() => {
    setReviewImage(null);
    setReviewPaused(false);
    autoCaptureTriggeredRef.current = false;
    setPerspectiveCorners(null);
    setPerspectiveApplied(false);
    setPerspectiveOriginal(null);
    if (restoreTorchOnReviewDismissRef.current) {
      restoreTorchOnReviewDismissRef.current = false;
      void setTorch(true);
    }
    void haptics.light();
  }, [setReviewPaused, setTorch]);

  const handleCancelReview = useCallback(() => {
    setReviewImage(null);
    autoCaptureTriggeredRef.current = false;
    setPerspectiveCorners(null);
    setPerspectiveApplied(false);
    setPerspectiveOriginal(null);
    close();
    void haptics.light();
  }, [close]);

  const handleRemoveBatchPhoto = useCallback((idx: number) => {
    setBatchPhotos(prev => prev.filter((_, i) => i !== idx));
    void haptics.light();
  }, []);

  const handleDoneBatch = useCallback(() => {
    if (captureModeState === 'multiple' && onBatchComplete) {
      // Multiple mode: each photo becomes a separate document
      onBatchComplete(batchPhotos);
    } else {
      // Combine mode: all photos become one multi-page document
      batchPhotos.forEach(photo => submitPhoto(photo));
    }
    onAfterCapture?.();
    close();
    void haptics.success();
  }, [onAfterCapture, close, batchPhotos, submitPhoto, captureModeState, onBatchComplete]);

  // ---- Review Buttons: captureAnotherDone mode handlers ----
  // handleCaptureAnother: saves the current review photo to the batch and
  // returns to the live camera for another capture.
  const handleCaptureAnother = useCallback(() => {
    if (reviewImage) {
      setBatchPhotos(prev => [...prev, reviewImage]);
    }
    setReviewImage(null);
    setReviewPaused(false);
    autoCaptureTriggeredRef.current = false;
    setPerspectiveCorners(null);
    setPerspectiveApplied(false);
    setPerspectiveOriginal(null);
    if (restoreTorchOnReviewDismissRef.current) {
      restoreTorchOnReviewDismissRef.current = false;
      void setTorch(true);
    }
    void haptics.medium();
  }, [reviewImage, setReviewPaused, setTorch]);

  // handleDoneReview: submits all batch photos plus the current review photo,
  // then closes the camera entirely — returning control to the parent form.
  const handleDoneReview = useCallback(() => {
    const allPhotos = [...batchPhotos];
    if (reviewImage) allPhotos.push(reviewImage);

    if (captureModeState === 'multiple' && onBatchComplete) {
      // Multiple mode: each photo becomes a separate document
      onBatchComplete(allPhotos);
    } else {
      // Combine mode: all photos become one multi-page document
      allPhotos.forEach(photo => submitPhoto(photo));
    }

    setReviewImage(null);
    setBatchPhotos([]);
    autoCaptureTriggeredRef.current = false;
    setPerspectiveCorners(null);
    setPerspectiveApplied(false);
    setPerspectiveOriginal(null);
    onAfterCapture?.();
    close();
    void haptics.success();
  }, [onAfterCapture, close, batchPhotos, reviewImage, submitPhoto, captureModeState, onBatchComplete]);

  // ---- Perspective Correction handlers ----
  const handleApplyPerspective = useCallback(async () => {
    if (!reviewImage || !perspectiveCorners) return;
    setPerspectiveApplying(true);
    try {
      // Store original if not already stored
      if (!perspectiveOriginal) {
        setPerspectiveOriginal(reviewImage);
      }
      // Convert relative corners to pixel coordinates using the image element
      const img = reviewImageRef.current;
      if (!img) {
        setPerspectiveApplying(false);
        return;
      }
      const natW = img.naturalWidth;
      const natH = img.naturalHeight;
      const pixelCorners: Point[] = perspectiveCorners.map(c => ({
        x: c.x * natW,
        y: c.y * natH,
      }));
      const corrected = await correctPerspectiveFromBase64(reviewImage, pixelCorners);
      setReviewImage(corrected);
      setPerspectiveApplied(true);
      void haptics.medium();
    } catch (err) {
      console.error('[Perspective] correction failed:', err);
    } finally {
      setPerspectiveApplying(false);
    }
  }, [reviewImage, perspectiveCorners, perspectiveOriginal]);

  const handleResetPerspective = useCallback(() => {
    if (perspectiveOriginal) {
      setReviewImage(perspectiveOriginal);
    }
    setPerspectiveApplied(false);
    void haptics.light();
    // Reset corners to default
    setPerspectiveCorners([
      { x: 0.1, y: 0.1 },
      { x: 0.9, y: 0.1 },
      { x: 0.9, y: 0.9 },
      { x: 0.1, y: 0.9 },
    ]);
  }, [perspectiveOriginal]);

  const handleCornerDrag = useCallback((cornerIdx: number, clientX: number, clientY: number) => {
    const img = reviewImageRef.current;
    if (!img || !perspectiveCorners) return;
    const rect = img.getBoundingClientRect();
    const relX = (clientX - rect.left) / rect.width;
    const relY = (clientY - rect.top) / rect.height;
    setPerspectiveCorners(prev => {
      if (!prev) return prev;
      const next = [...prev];
      next[cornerIdx] = {
        x: Math.max(0, Math.min(1, relX)),
        y: Math.max(0, Math.min(1, relY)),
      };
      return next;
    });
  }, [perspectiveCorners]);

  // Track the rendered position of the review <img> inside its flex container
  // so that perspective overlays (SVG polygon + corner handles) can be
  // positioned relative to the image rather than the container.  With
  // object-contain the image may be letterboxed, so container-relative
  // percentages would be wrong.
  const updateReviewImgLayout = useCallback(() => {
    const img = reviewImageRef.current;
    const container = reviewContainerRef.current;
    if (!img || !container) return;
    const imgRect = img.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    setReviewImgLayout({
      left: imgRect.left - containerRect.left,
      top: imgRect.top - containerRect.top,
      width: imgRect.width,
      height: imgRect.height,
    });
  }, []);

  useEffect(() => {
    if (!reviewImage) {
      setReviewImgLayout(null);
      return;
    }
    const img = reviewImageRef.current;
    if (img) {
      if (img.complete) updateReviewImgLayout();
      img.addEventListener('load', updateReviewImgLayout);
    }
    window.addEventListener('resize', updateReviewImgLayout);
    window.addEventListener('orientationchange', updateReviewImgLayout);
    return () => {
      window.removeEventListener('resize', updateReviewImgLayout);
      window.removeEventListener('orientationchange', updateReviewImgLayout);
      img?.removeEventListener('load', updateReviewImgLayout);
    };
  }, [reviewImage, updateReviewImgLayout]);

  // ---- Auto-Capture logic ----
  // Tracks stability of the document edge over time. When the edge box
  // has low variance and sufficient confidence for a sustained period,
  // a countdown begins. If stability holds through the countdown, the
  // photo is captured automatically — no manual tap required.

  // Reset auto-capture + auto-torch + auto-zoom state when camera closes
  useEffect(() => {
    if (!isOpen) {
      edgeHistoryRef.current = [];
      stableSinceRef.current = null;
      setCountdown(null);
      autoCaptureTriggeredRef.current = false;
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
        countdownTimerRef.current = null;
      }
      // M2: Reset torch mode state
      autoTorchPendingRef.current = false;
      if (autoTorchOffTimerRef.current) {
        clearTimeout(autoTorchOffTimerRef.current);
        autoTorchOffTimerRef.current = null;
      }
      // Explicitly turn off the physical torch when the camera closes.
      // The auto-torch effect may have a pending toggleTorch() call that
      // completes after the stream is stopped, leaving the physical torch
      // on. This ensures the torch is off regardless of in-flight state.
      if (torchEnabled) {
        void setTorch(false);
      }
      // M3: Reset auto-zoom state
      setAutoZoomOn(false);
      settledSinceRef.current = null;
      noEdgeSinceRef.current = null;
      if (autoZoomRafRef.current) {
        cancelAnimationFrame(autoZoomRafRef.current);
        autoZoomRafRef.current = null;
      }
      // Readability gate: clear blocked-capture hint + force window
      setCaptureGateReason(null);
      forceCaptureUntilRef.current = 0;
    }
  }, [isOpen, torchEnabled, setTorch]);

  // ---- Torch mode: 'auto' hysteresis effect (M2) ----
  // When torchMode is 'auto', watch ambient brightness and toggle torch with
  // hysteresis. Low threshold turns torch on immediately; high threshold
  // turns torch off only after a sustained 3-second debounce to prevent
  // oscillation when a white document reflects torch light back.
  // 'on' and 'off' modes are handled directly in handleTorchToggle (which
  // calls toggleTorch immediately), so no effects needed for those.
  // Uses toggleTorchRef (not toggleTorch) to avoid effect re-runs every frame
  // during auto-zoom (toggleTorch depends on zoomLevel).
  // Note: we don't gate on torchSupported — detectTorchSupport is unreliable
  // on some devices (e.g. Samsung S24 WebView) and may return false even when
  // torch is supported via alternate camera recovery in toggleTorch.
  useEffect(() => {
    if (!isOpen || !stream || torchMode !== 'auto' || isReviewPausedRef.current) return;
    if (autoTorchPendingRef.current) return;

    // Torch triggers in dim indoor lighting, not just near-darkness.
    // Luminance is 0–255; 100 ≈ 39% brightness (typical indoor room lit by
    // a single overhead light). The high threshold (160) provides a wide
    // hysteresis band so the torch doesn't flicker off when the white receipt
    // reflects torch light back at the sensor.
    const HYSTERESIS_LOW = 100;
    const HYSTERESIS_HIGH = 160;
    const OFF_DEBOUNCE_MS = 3000;

    if (ambientBrightness !== null && ambientBrightness < HYSTERESIS_LOW && !torchEnabled) {
      // Low light + torch off → turn torch on immediately
      // Cancel any pending off-timer (shouldn't exist if torch is off, but safe)
      if (autoTorchOffTimerRef.current) {
        clearTimeout(autoTorchOffTimerRef.current);
        autoTorchOffTimerRef.current = null;
      }
      autoTorchPendingRef.current = true;
      void toggleTorchRef.current().finally(() => { autoTorchPendingRef.current = false; });
    } else if (ambientBrightness !== null && ambientBrightness > HYSTERESIS_HIGH && torchEnabled) {
      // Bright + torch on → start debounce timer before turning off.
      // If brightness drops back below HIGH before the timer fires (e.g. the
      // white document moved away), the timer is cancelled by the else branch
      // below on the next brightness sample.
      if (!autoTorchOffTimerRef.current) {
        autoTorchOffTimerRef.current = setTimeout(() => {
          autoTorchOffTimerRef.current = null;
          if (autoTorchPendingRef.current) return;
          if (torchModeRef.current !== 'auto') return;
          autoTorchPendingRef.current = true;
          void toggleTorchRef.current().finally(() => { autoTorchPendingRef.current = false; });
        }, OFF_DEBOUNCE_MS);
      }
    } else {
      // Brightness is in the hysteresis band or conditions don't warrant
      // toggling — cancel any pending off-timer.
      if (autoTorchOffTimerRef.current) {
        clearTimeout(autoTorchOffTimerRef.current);
        autoTorchOffTimerRef.current = null;
      }
    }
  }, [ambientBrightness, torchEnabled, isOpen, stream, torchMode, reviewPaused]);

  // Sync the toggle with prop changes and restore from localStorage on open
  useEffect(() => {
    if (isOpen) {
      if (!autoCaptureEnabled) {
        // Auto-capture is explicitly disabled by the prop (e.g.
        // LicenseDiskScanner, casual wage photo). Don't restore from
        // localStorage — the consumer explicitly opted out.
        setAutoCaptureOn(false);
      } else {
        try {
          const stored = localStorage.getItem(AUTO_CAPTURE_PREF_KEY);
          setAutoCaptureOn(stored === null ? autoCaptureEnabled : stored === 'true');
        } catch {
          setAutoCaptureOn(autoCaptureEnabled);
        }
      }
    } else {
      setAutoCaptureOn(autoCaptureEnabled);
    }
  }, [autoCaptureEnabled, isOpen, AUTO_CAPTURE_PREF_KEY]);

  // Sync auto-zoom toggle with prop changes and restore from localStorage on open.
  // The stored user preference takes precedence when set (the user explicitly
  // toggled auto-zoom off via the "Auto" button or floating toggle). When no
  // stored value exists, the prop is used as the default. This matches the
  // auto-capture sync behavior and ensures the user can permanently disable
  // auto-zoom even when the parent component enables it by default.
  useEffect(() => {
    if (isOpen) {
      if (!autoZoomEnabled) {
        // Auto-zoom is explicitly disabled by the prop (e.g.
        // LicenseDiskScanner, casual wage photo). Don't restore from
        // localStorage — the consumer explicitly opted out.
        setAutoZoomOn(false);
      } else {
        try {
          const stored = localStorage.getItem(AUTO_ZOOM_PREF_KEY);
          setAutoZoomOn(stored === null ? true : stored === 'true');
        } catch {
          setAutoZoomOn(true);
        }
      }
    } else {
      setAutoZoomOn(autoZoomEnabled || false);
    }
  }, [autoZoomEnabled, isOpen, AUTO_ZOOM_PREF_KEY]);

  // Sync torch mode with localStorage on open. When darkModeEnhance is true,
  // default to 'auto' (torch controlled by ambient brightness). Otherwise
  // default to 'off'. The stored preference takes precedence when set.
  useEffect(() => {
    if (isOpen) {
      try {
        const stored = localStorage.getItem(AUTO_TORCH_PREF_KEY);
        const mode: TorchMode = (stored === 'off' || stored === 'on' || stored === 'auto')
          ? stored
          : (darkModeEnhance ? 'auto' : 'off');
        setTorchMode(mode);
        torchModeRef.current = mode;
      } catch {
        const mode: TorchMode = darkModeEnhance ? 'auto' : 'off';
        setTorchMode(mode);
        torchModeRef.current = mode;
      }
    } else {
      setTorchMode('off');
      torchModeRef.current = 'off';
    }
  }, [darkModeEnhance, isOpen, AUTO_TORCH_PREF_KEY]);

  // Apply torch state when the camera opens or torch support is detected.
  // torchSupported is set asynchronously ~500ms after camera open by
  // scheduleTrackSetup. But detectTorchSupport is unreliable on some devices
  // (e.g. Samsung S24 WebView) — it may return false even when torch is
  // supported via alternate camera recovery. So we don't gate on
  // torchSupported; we call toggleTorch whenever torchMode='on' and the
  // torch isn't on yet. toggleTorch includes recovery logic that probes
  // alternate cameras if the initial track doesn't support torch.
  //  - 'on'  → call toggleTorch() to turn torch on
  //  - 'auto' → let the hysteresis effect handle it
  //  - 'off'  → ensure torch is off
  // Uses toggleTorchRef (not toggleTorch) to avoid effect re-runs every frame
  // during auto-zoom (toggleTorch depends on zoomLevel).
  useEffect(() => {
    if (!isOpen || !stream || isReviewPausedRef.current) return;
    if (autoTorchPendingRef.current) return;
    const mode = torchModeRef.current;
    if (mode === 'on' && !torchEnabled) {
      autoTorchPendingRef.current = true;
      void toggleTorchRef.current().finally(() => { autoTorchPendingRef.current = false; });
    } else if (mode === 'off' && torchEnabled) {
      autoTorchPendingRef.current = true;
      void toggleTorchRef.current().finally(() => { autoTorchPendingRef.current = false; });
    }
    // 'auto' mode is handled by the hysteresis effect above
  }, [isOpen, stream, torchSupported, torchEnabled, torchMode, reviewPaused]);

  useEffect(() => {
    if (!isOpen || !effectiveAutoCapture || !(isAutoMode || isVehicleMode) || !edgeDetectionEnabled) {
      setCountdown(null);
      stableSinceRef.current = null;
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
        countdownTimerRef.current = null;
      }
      return;
    }

    // When auto-zoom is also enabled, auto-capture must wait for the zoom to
    // settle. A zoom animation in progress (autoZoomRafRef !== null) or an
    // unsettled zoom level (settledSinceRef === null) means the frame is still
    // adjusting — treat it like movement and reset the stability tracker.
    const zoomAdjusting = autoZoomOn && (autoZoomRafRef.current !== null || settledSinceRef.current === null);
    if (zoomAdjusting) {
      stableSinceRef.current = null;
      edgeHistoryRef.current = [];
      if (countdown !== null) {
        setCountdown(null);
        if (countdownTimerRef.current) {
          clearInterval(countdownTimerRef.current);
          countdownTimerRef.current = null;
        }
      }
      return;
    }

    const now = performance.now();
    const edge = documentEdge;

    // When ML detection is active, use ML confidence to drive auto-capture.
    // The ML model (DocAligner) detects actual document corners and is far
    // more accurate than the classical brightness-gradient detector for
    // long slips, partial documents, and low-contrast scenes.
    // The classical detector still provides the edge box for display and
    // serves as a fallback when ML is not initialized.
    const mlEdgeCurrent = mlEdgeRef.current;
    const mlConfidence = mlEdgeCurrent?.confidence ?? 0;
    const effectiveConfidence = Math.max(edge?.confidence ?? 0, mlConfidence);

    // Validate that the detected edge looks like an actual receipt/document
    // rather than a random brightness transition in an empty scene.
    // When ML is active, we trust the ML confidence directly — it's based
    // on a trained model, not brightness gradients.
    // When ML is not active, we apply stricter classical-detection filters.
    const looksLikeReceipt = (e: DocumentEdge | null): e is DocumentEdge => {
      if (!e) {
        // If ML detected a document but classical didn't, still accept it.
        // ML confidence alone is sufficient when the model is active.
        if (mlConfidence >= autoCaptureConfidence) return true;
        return false;
      }
      // Use the higher of classical/ML confidence
      if (effectiveConfidence <= autoCaptureConfidence) return false;
      const area = e.width * e.height;
      // Lower area threshold for long slips that fill the height but are
      // narrow (e.g. 20% width × 95% height = 0.19 area). These are valid
      // partial captures of a long till slip.
      if (area < 0.08) return false;       // too small — likely noise
      // Allow documents that fill most of the frame (long slips extend
      // beyond the frame in one dimension). Only reject if BOTH dimensions
      // fill 98%+ (no document visible against background at all).
      if (e.width > 0.98 && e.height > 0.98) return false;
      // Allow tall aspect ratios for long till slips (up to 10:1).
      // Classical detector may report the visible portion only.
      if (e.aspectRatio < 0.15 || e.aspectRatio > 10.0) return false;
      // Centering: the edge box center should be within ~35% of frame center.
      // Relaxed from 30% to account for long slips that extend beyond frame.
      // For documents that fill the full height (height > 0.90), relax the
      // Y centering check entirely — the slip extends beyond the frame so
      // the center is meaningless.
      const centerX = e.x + e.width / 2;
      const centerY = e.y + e.height / 2;
      if (Math.abs(centerX - 0.5) > 0.35) return false;
      if (e.height < 0.90 && Math.abs(centerY - 0.5) > 0.35) return false;
      return true;
    };

    // Readability gate — auto-capture must never fire on a slip OCR can't
    // read (too far away / blurry). An unreadable verdict, or an edge that
    // hasn't been assessed yet, is treated as unstable and resets tracking.
    if (requireReadableDocument && isAutoMode) {
      const rb = readabilityRef.current;
      if (!rb || !rb.readable) {
        edgeHistoryRef.current = [];
        stableSinceRef.current = null;
        if (countdown !== null) {
          setCountdown(null);
          if (countdownTimerRef.current) {
            clearInterval(countdownTimerRef.current);
            countdownTimerRef.current = null;
          }
        }
        return;
      }
    }

    if (looksLikeReceipt(edge)) {
      // Add sample to history (keep last 12)
      edgeHistoryRef.current.push({ x: edge?.x ?? 0.5, y: edge?.y ?? 0.5, w: edge?.width ?? 0, h: edge?.height ?? 0, c: effectiveConfidence, t: now });
      if (edgeHistoryRef.current.length > 12) edgeHistoryRef.current.shift();

      // Need at least 4 samples (~800ms at 200ms interval) to assess stability.
      // Reduced from 8 to make auto-capture more responsive — the ML confidence
      // is already a strong signal, so we don't need as many samples to confirm.
      const samples = edgeHistoryRef.current;
      if (samples.length >= 4) {
        // Require consistent confidence across ALL samples — if any sample
        // dipped below the threshold, the detection is unreliable.
        const allConfident = samples.every(s => s.c > autoCaptureConfidence);
        if (!allConfident) {
          stableSinceRef.current = null;
          if (countdown !== null) {
            setCountdown(null);
            if (countdownTimerRef.current) {
              clearInterval(countdownTimerRef.current);
              countdownTimerRef.current = null;
            }
          }
          return;
        }

        // Compute variance of position and size
        const xs = samples.map(s => s.x);
        const ys = samples.map(s => s.y);
        const ws = samples.map(s => s.w);
        const hs = samples.map(s => s.h);
        const variance = (arr: number[]) => {
          const mean = arr.reduce((a, b) => a + b, 0) / arr.length;
          return arr.reduce((a, b) => a + (b - mean) ** 2, 0) / arr.length;
        };
        const maxVar = Math.max(variance(xs), variance(ys), variance(ws), variance(hs));

        // Stable if variance is low enough (edge box isn't jumping around).
        // Relaxed from 0.0008 to 0.003 to allow natural hand movement
        // while still rejecting large jumps (document entering/leaving frame).
        const isStable = maxVar < 0.003;

        if (isStable) {
          if (stableSinceRef.current === null) {
            stableSinceRef.current = now;
          }

          const stableDuration = now - stableSinceRef.current;

          // Start countdown if stable for long enough and not already counting down
          if (stableDuration >= autoCaptureStabilityMs && countdown === null && !autoCaptureTriggeredRef.current) {
            setCountdown(autoCaptureCountdownSec);
            countdownTimerRef.current = setInterval(() => {
              setCountdown(prev => {
                if (prev === null) return null;
                if (prev <= 1) {
                  // Countdown reached zero — trigger capture
                  if (countdownTimerRef.current) {
                    clearInterval(countdownTimerRef.current);
                    countdownTimerRef.current = null;
                  }
                  autoCaptureTriggeredRef.current = true;
                  // Defer capture to next tick to avoid setState during render
                  setTimeout(() => {
                    void handleCaptureRef.current();
                  }, 0);
                  return null;
                }
                return prev - 1;
              });
            }, 1000);
          }
        } else {
          // Unstable — reset stability timer and cancel countdown
          stableSinceRef.current = null;
          if (countdown !== null) {
            setCountdown(null);
            if (countdownTimerRef.current) {
              clearInterval(countdownTimerRef.current);
              countdownTimerRef.current = null;
            }
          }
        }
      }
    } else {
      // No receipt-like edge detected (no edge, low confidence, implausible
      // size/aspect ratio) — reset everything
      edgeHistoryRef.current = [];
      stableSinceRef.current = null;
      if (countdown !== null) {
        setCountdown(null);
        if (countdownTimerRef.current) {
          clearInterval(countdownTimerRef.current);
          countdownTimerRef.current = null;
        }
      }
    }
  }, [documentEdge, mlEdgeWithCorners, isOpen, effectiveAutoCapture, isAutoMode, isVehicleMode, edgeDetectionEnabled, autoCaptureConfidence, autoCaptureStabilityMs, autoCaptureCountdownSec, countdown, autoZoomOn, requireReadableDocument]);

  // Clear the blocked-capture hint once the 4s force-capture window expires,
  // and immediately once the detected document becomes readable again.
  useEffect(() => {
    if (captureGateReason === null) return;
    const t = setTimeout(() => setCaptureGateReason(null), 4000);
    return () => clearTimeout(t);
  }, [captureGateReason]);

  useEffect(() => {
    if (docReadability?.readable) setCaptureGateReason(null);
  }, [docReadability?.readable]);

  // Haptic tick each time a new auto-capture countdown number appears (3…2…1).
  // The capture itself fires haptics.heavy() inside handleCapture.
  const lastCountdownTickRef = useRef<number | null>(null);
  useEffect(() => {
    if (countdown === null) {
      lastCountdownTickRef.current = null;
      return;
    }
    if (countdown !== lastCountdownTickRef.current) {
      lastCountdownTickRef.current = countdown;
      void haptics.tick();
    }
  }, [countdown]);

  // Cleanup countdown timer on unmount
  useEffect(() => {
    return () => {
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
      }
      if (autoTorchOffTimerRef.current) {
        clearTimeout(autoTorchOffTimerRef.current);
      }
    };
  }, []);

  // Page visibility guard — turn off the torch when the app is backgrounded.
  // On some devices (especially Capacitor WebView), the torch stays on
  // physically when the app goes to the background, even if the camera
  // stream is still active. This ensures the torch is off when the user
  // switches to another app or minimizes the browser.
  useEffect(() => {
    if (!isOpen || typeof document === 'undefined') return;
    const handleVisibilityChange = () => {
      if (document.hidden && torchEnabledRef.current) {
        console.log('[CameraModal] Page hidden — turning off torch');
        autoTorchPendingRef.current = false;
        if (autoTorchOffTimerRef.current) {
          clearTimeout(autoTorchOffTimerRef.current);
          autoTorchOffTimerRef.current = null;
        }
        void setTorch(false);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isOpen, setTorch]);

  // Focus ring animation
  const [focusRing, setFocusRing] = useState<{ x: number; y: number; visible: boolean }>({ x: 0, y: 0, visible: false });
  const focusTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Pinch-to-zoom tracking
  const pinchStartDistRef = useRef<number | null>(null);
  const pinchStartZoomRef = useRef<number>(1);

  // ---- Auto-Zoom: smooth zoom transition helper (M3) ----
  // Uses zoomLevelRef (not zoomLevel state) so the callback identity is stable
  // and doesn't trigger the controller effect on every animation frame.
  const smoothZoomTo = useCallback((targetZoom: number) => {
    if (autoZoomRafRef.current) cancelAnimationFrame(autoZoomRafRef.current);

    const startZoom = zoomLevelRef.current;
    const startTime = performance.now();
    const duration = 500;

    const animate = () => {
      const elapsed = performance.now() - startTime;
      const t = Math.min(1, elapsed / duration);
      const eased = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
      const current = startZoom + (targetZoom - startZoom) * eased;
      void setZoom(current);

      if (t < 1) {
        autoZoomRafRef.current = requestAnimationFrame(animate);
      } else {
        autoZoomRafRef.current = null;
      }
    };
    autoZoomRafRef.current = requestAnimationFrame(animate);
  }, [setZoom]);

  // ---- Auto-Zoom: search cycle when no edge detected (M3) ----
  // Sweeps from zoomMin up to zoomMax and back, repeating, until an edge is
  // detected. Uses documentEdgeRef and mlEdgeRef (not the state) so the
  // callback identity is stable and doesn't restart the sweep every 200ms.
  const runZoomCycle = useCallback(() => {
    if (autoZoomRafRef.current) cancelAnimationFrame(autoZoomRafRef.current);

    const startTime = performance.now();
    const cycleDuration = 6000;
    const zoomRange = zoomMax - zoomMin;

    const animate = () => {
      const elapsed = performance.now() - startTime;
      const cycleT = (elapsed % cycleDuration) / cycleDuration;
      const wave = cycleT < 0.5 ? cycleT * 2 : 2 - cycleT * 2;
      const target = zoomMin + zoomRange * wave;
      void setZoom(target);

      // Check both ML and classical edges — ML is more stable during zoom
      const mlEdgeForZoom = mlEdgeRef.current;
      const edge = mlEdgeForZoom ?? documentEdgeRef.current;
      if (edge && edge.confidence > autoZoomConfidence) {
        autoZoomRafRef.current = null;
        return;
      }

      autoZoomRafRef.current = requestAnimationFrame(animate);
    };
    autoZoomRafRef.current = requestAnimationFrame(animate);
  }, [zoomMin, zoomMax, setZoom, autoZoomConfidence]);

  // ---- Auto-Zoom: zoom-out search when edge is lost (M3) ----
  // Self-scheduling animation that smoothly zooms out from the current zoom
  // level to zoomMin, checking for edge reappearance on every frame. If an
  // edge is found, it stops and lets the controller effect take over. If it
  // reaches zoomMin without finding an edge, it transitions to the full
  // sweep cycle (runZoomCycle). This is self-scheduling (via rAF) so it
  // doesn't rely on the controller effect re-running — which it won't when
  // documentEdge stays null.
  const zoomOutToFind = useCallback(() => {
    if (autoZoomRafRef.current) cancelAnimationFrame(autoZoomRafRef.current);

    const startZoom = zoomLevelRef.current;
    const startTime = performance.now();
    const duration = 1500; // 1.5s to zoom out — fast enough to be responsive

    const animate = () => {
      const elapsed = performance.now() - startTime;
      const t = Math.min(1, elapsed / duration);
      const eased = 1 - Math.pow(1 - t, 3); // ease-out cubic
      const current = startZoom + (zoomMin - startZoom) * eased;
      void setZoom(current);

      // Check both ML and classical edges — ML is more stable during zoom
      const mlEdgeForZoom = mlEdgeRef.current;
      const edge = mlEdgeForZoom ?? documentEdgeRef.current;
      if (edge && edge.confidence > autoZoomConfidence) {
        autoZoomRafRef.current = null;
        return; // controller effect will take over
      }

      if (t >= 1) {
        // Reached zoomMin without finding an edge — start full sweep
        runZoomCycle();
        return;
      }

      autoZoomRafRef.current = requestAnimationFrame(animate);
    };
    autoZoomRafRef.current = requestAnimationFrame(animate);
  }, [zoomMin, setZoom, autoZoomConfidence, runZoomCycle]);

  // ---- Auto-Zoom: controller effect (M3) ----
  // Reacts to edge detection results (every ~200ms) and adjusts zoom.
  // Uses ML edge when available (more stable during zoom animations),
  // falls back to classical edge.
  // IMPORTANT: zoomLevel is NOT in the dependency array — we read it via
  // zoomLevelRef instead. This prevents the effect from re-running on every
  // animation frame of smoothZoomTo (which would cancel and restart the
  // animation, making zoom-out imperceptibly slow).
  useEffect(() => {
    if (!isOpen || !autoZoomOn || !(isAutoMode || isVehicleMode) || !edgeDetectionEnabled || reviewImage) {
      if (autoZoomRafRef.current) {
        cancelAnimationFrame(autoZoomRafRef.current);
        autoZoomRafRef.current = null;
      }
      noEdgeSinceRef.current = null;
      return;
    }

    if (pinchStartDistRef.current !== null) return;

    const now = performance.now();
    // Prefer ML edge for auto-zoom — it's more stable during zoom
    // animations and detects actual document corners, not just
    // brightness gradients that get disrupted by digital zoom scaling.
    const mlEdgeForZoom = mlEdgeRef.current;
    const edge = mlEdgeForZoom ?? documentEdge;
    const currentZoom = zoomLevelRef.current;

    if (edge && edge.confidence > autoZoomConfidence) {
      noEdgeSinceRef.current = null;

      const fillRatio = edge.width;

      // For till slips (long narrow receipts), use a lower fill target so
      // the auto-zoom doesn't over-zoom and push the slip edges out of
      // frame. The ML model can only detect corners it can see, so if the
      // zoom pushes the slip beyond the frame edges, the bounding box
      // shrinks to the visible portion (~50% of the actual slip).
      const effectiveTargetFill = documentProfile === 'till-slip'
        ? Math.min(autoZoomTargetFill, 0.55)
        : autoZoomTargetFill;
      // Cap max zoom for till slips — long receipts need less zoom.
      const effectiveZoomMax = documentProfile === 'till-slip'
        ? Math.min(zoomMax, 2.0)
        : zoomMax;

      // Compute the target zoom level. The formula differs by zoom type:
      //  - Native zoom: the video feed changes when zoom changes, so
      //    edge.width tracks the zoom. Use a relative adjustment:
      //    desiredZoom = currentZoom * (targetFill / fillRatio)
      //  - Digital zoom: only a CSS scale is applied; the raw video feed
      //    doesn't change, so edge.width is constant regardless of zoom.
      //    The displayed fill = fillRatio * zoomLevel, so we need an
      //    absolute target: desiredZoom = targetFill / fillRatio
      //    Using the relative formula here would multiply currentZoom by
      //    the same factor every 200ms, diverging to zoomMax/zoomMin.
      const desiredZoom = isDigitalZoomRef.current
        ? effectiveTargetFill / fillRatio
        : currentZoom * (effectiveTargetFill / fillRatio);
      const clampedZoom = Math.min(Math.max(desiredZoom, zoomMin), effectiveZoomMax);

      const ratio = currentZoom > 0 ? clampedZoom / currentZoom : 1;
      if (ratio > 0.85 && ratio < 1.15) {
        if (settledSinceRef.current === null) settledSinceRef.current = now;
        return;
      }

      // If a zoom animation is already in progress, let it complete before
      // starting a new one. Restarting every 200ms (on each documentEdge
      // update) prevents the animation from ever finishing, and because edge
      // detection lags behind the zoom (EMA smoothing + 200ms interval), the
      // target keeps receding — racing to zoomMax.
      if (autoZoomRafRef.current) return;

      settledSinceRef.current = null;
      smoothZoomTo(clampedZoom);
    } else {
      // No edge detected (or confidence too low). This could mean:
      //  (a) We over-zoomed and the document now fills/exceeds the frame,
      //      causing edge detection to fail.
      //  (b) There's genuinely no document in view.
      // In either case, start a self-scheduling zoom-out search that smoothly
      // zooms to zoomMin checking for edge reappearance, then transitions to
      // a full sweep if nothing is found. This is self-scheduling via rAF so
      // it doesn't depend on this effect re-running (which it won't when
      // documentEdge stays null).
      if (noEdgeSinceRef.current === null) {
        noEdgeSinceRef.current = now;
      }

      // Only start the zoom-out if there isn't already an animation running
      // (the zoom-out or sweep schedules itself via rAF).
      if (!autoZoomRafRef.current && currentZoom > zoomMin + 0.01) {
        zoomOutToFind();
      } else if (!autoZoomRafRef.current) {
        // Already at zoomMin with no edge — start the full sweep
        runZoomCycle();
      }
    }
  }, [documentEdge, mlEdgeWithCorners, isOpen, autoZoomOn, isAutoMode, isVehicleMode, edgeDetectionEnabled, reviewImage, zoomMin, zoomMax, autoZoomConfidence, autoZoomTargetFill, smoothZoomTo, runZoomCycle, zoomOutToFind, documentProfile]);

  // Reset tracking when modal closes
  useEffect(() => {
    if (!isOpen) {
      hasStartedRef.current = false;
    }
  }, [isOpen]);

  // Lock body scroll when camera is open (prevents layout issues on orientation change)
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('camera-modal-open');
    } else {
      document.body.classList.remove('camera-modal-open');
    }
    return () => {
      document.body.classList.remove('camera-modal-open');
    };
  }, [isOpen]);

  // Preferred orientation: lock screen to landscape/portrait on Capacitor/mobile
  // when the camera opens, and unlock when it closes. On web, the lock is
  // best-effort (Web Screen Orientation API) and may be silently ignored.
  // The rotate hint overlay (below) covers the case where the lock fails.
  useEffect(() => {
    if (!isOpen || !preferredOrientation || preferredOrientation === 'portrait') return;

    let cancelled = false;
    (async () => {
      try {
        const { ScreenOrientation } = await import('@/plugins/ScreenOrientation');
        if (cancelled) return;
        await ScreenOrientation.lock({ orientation: preferredOrientation });
      } catch {
        // Lock not supported on this platform — the rotate hint will guide the user.
      }
    })();

    return () => {
      cancelled = true;
      (async () => {
        try {
          const { ScreenOrientation } = await import('@/plugins/ScreenOrientation');
          await ScreenOrientation.unlock();
        } catch {
          // ignore
        }
      })();
    };
  }, [isOpen, preferredOrientation]);

  // Whether the user should see a "rotate to landscape" hint.
  // Shown when landscape is preferred but the phone is currently in portrait.
  const showRotateHint = preferredOrientation === 'landscape' && !isLandscape && !reviewImage;

  // ---- Keyboard & Hardware Shortcuts ----
  // Space = capture, Escape = close (desktop/web)
  // Volume buttons = capture (mobile, via keydown listener)
  const handleShortcutKey = useCallback((e: KeyboardEvent) => {
    if (!isOpen || reviewImage) return;
    // Escape closes the modal
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
      return;
    }
    // Space or Enter triggers capture
    if (e.key === ' ' || e.key === 'Enter') {
      // Don't intercept if focus is on a button/input
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') return;
      e.preventDefault();
      void handleCaptureRef.current();
      return;
    }
    // Volume up/down buttons trigger capture on mobile browsers
    if (hardwareCapture && (e.key === 'AudioVolumeUp' || e.key === 'AudioVolumeDown')) {
      e.preventDefault();
      void handleCaptureRef.current();
    }
  }, [isOpen, reviewImage, close, hardwareCapture]);

  useEffect(() => {
    if (!isOpen) return;
    window.addEventListener('keydown', handleShortcutKey);
    return () => window.removeEventListener('keydown', handleShortcutKey);
  }, [handleShortcutKey, isOpen]);


  const videoCallbackRef = useCallback(
    (node: HTMLVideoElement | null) => {
      attachVideoElement(node);
    },
    [attachVideoElement],
  );

  useEffect(() => {
    if (!isOpen || hasStartedRef.current || stream || isLoading || isTorchChecking) {
      return;
    }

    console.log('[CameraModal] Modal opened, ensuring camera starts...');
    hasStartedRef.current = true;
    void startCamera(facingMode);
  }, [facingMode, isLoading, isOpen, isTorchChecking, startCamera, stream]);

  // Tap-to-focus handler for the video container
  const handleVideoTap = useCallback(
    (e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
      const target = e.currentTarget;
      const rect = target.getBoundingClientRect();
      let clientX: number, clientY: number;

      if ('touches' in e) {
        // Ignore multi-touch (pinch)
        if (e.touches.length > 1) return;
        clientX = e.touches[0].clientX;
        clientY = e.touches[0].clientY;
      } else {
        clientX = e.clientX;
        clientY = e.clientY;
      }

      const x = (clientX - rect.left) / rect.width;
      const y = (clientY - rect.top) / rect.height;

      // Show focus ring animation
      setFocusRing({ x: clientX - rect.left, y: clientY - rect.top, visible: true });
      if (focusTimerRef.current) clearTimeout(focusTimerRef.current);
      focusTimerRef.current = setTimeout(() => setFocusRing(prev => ({ ...prev, visible: false })), 1200);

      void haptics.light();
      // In manual focus mode, lock the focus point. In AF mode, focus and
      // then return to continuous AF after the default delay.
      void tapToFocus(x, y, !autofocusActive);
    },
    [tapToFocus, autofocusActive],
  );

  // Disable auto-zoom as soon as the user takes manual control of zoom.
  const disableAutoZoom = useCallback(() => {
    if (!autoZoomOn) return;
    setAutoZoomOn(false);
    try { localStorage.setItem(AUTO_ZOOM_PREF_KEY, 'false'); } catch { /* ignore */ }
    void haptics.selection();
    if (autoZoomRafRef.current) {
      cancelAnimationFrame(autoZoomRafRef.current);
      autoZoomRafRef.current = null;
    }
    settledSinceRef.current = null;
    noEdgeSinceRef.current = null;
    // Reset auto-capture stability so it doesn't fire while the user
    // is manually adjusting zoom.
    setCountdown(null);
    stableSinceRef.current = null;
    edgeHistoryRef.current = [];
    autoCaptureTriggeredRef.current = false;
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
  }, [autoZoomOn, setAutoZoomOn, setCountdown]);

  // Pinch-to-zoom handlers
  const handleTouchStart = useCallback(
    (e: React.TouchEvent<HTMLDivElement>) => {
      if (e.touches.length === 2 && zoomSupported) {
        disableAutoZoom();
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        pinchStartDistRef.current = Math.hypot(dx, dy);
        pinchStartZoomRef.current = zoomLevel;
      }
    },
    [disableAutoZoom, zoomSupported, zoomLevel],
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent<HTMLDivElement>) => {
      if (e.touches.length === 2 && pinchStartDistRef.current !== null && zoomSupported) {
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        const dist = Math.hypot(dx, dy);
        const scale = dist / pinchStartDistRef.current;
        const newZoom = Math.min(Math.max(pinchStartZoomRef.current * scale, zoomMin), zoomMax);
        setZoom(newZoom);
      }
    },
    [zoomSupported, zoomMin, zoomMax, setZoom],
  );

  const handleTouchEnd = useCallback(() => {
    pinchStartDistRef.current = null;
  }, []);

  // Zoom display text
  const zoomText = useMemo(() => {
    if (!zoomSupported) return null;
    return `${zoomLevel.toFixed(1)}x`;
  }, [zoomSupported, zoomLevel]);

  // Compute the on-screen position of the dynamic document edge box.
  // Maps from video-relative coordinates (0–1) to container pixel coordinates,
  // accounting for object-cover scaling/cropping.
  // Must be called before the early return to satisfy rules-of-hooks.
  const dynamicBoxPosition = useMemo((): CSSProperties | null => {
    if (!displayEdge || !videoRef.current || !videoContainerRef.current) return null;
    const video = videoRef.current;
    const container = videoContainerRef.current;
    if (!video.videoWidth || !video.videoHeight) return null;

    const containerRect = container.getBoundingClientRect();
    const vw = video.videoWidth;
    const vh = video.videoHeight;
    const scale = Math.max(containerRect.width / vw, containerRect.height / vh);
    const displayW = vw * scale;
    const displayH = vh * scale;
    const offsetX = (containerRect.width - displayW) / 2;
    const offsetY = (containerRect.height - displayH) / 2;

    // When digital zoom is active the video element is CSS-scaled about
    // the container center — apply the same transform to the box so it
    // tracks the document where it actually appears on screen.
    const z = isDigitalZoom ? zoomLevel : 1;
    const cx = containerRect.width / 2;
    const cy = containerRect.height / 2;

    return {
      position: 'absolute',
      left: cx + (offsetX + displayEdge.x * displayW - cx) * z,
      top: cy + (offsetY + displayEdge.y * displayH - cy) * z,
      width: displayEdge.width * displayW * z,
      height: displayEdge.height * displayH * z,
    };
  }, [displayEdge, videoRef, isDigitalZoom, zoomLevel]);

  const shouldShowTorchControl = facingMode === 'environment' && (torchSupported || torchEnabled || !isLoading);
  const shouldShowSwitchCameraControl = canSwitchCamera || availableCameraCount === 0;
  const switchCameraLabel = facingMode === 'user' ? 'Switch to rear camera' : 'Switch to front camera';

  if (!isOpen) return null;

  // ---- Shared sub-components ----

  const videoElement = (
    <video
      ref={videoCallbackRef}
      autoPlay
      playsInline
      muted
      className="w-full h-full object-cover"
      style={{
        transform: `${facingMode === 'user' ? 'scaleX(-1) ' : ''}scale(${isDigitalZoom ? zoomLevel : 1})`,
        transformOrigin: 'center center',
      }}
    />
  );

  const loadingOverlay = isLoading && (
    <div className="absolute inset-0 bg-black flex flex-col items-center justify-center z-10">
      <div className="relative mb-4">
        <Image src={EMBEDDED_FAVICON} alt="Loading" width={64} height={64} unoptimized className="w-16 h-16" />
        <div className="absolute inset-0 -m-1.5 w-[76px] h-[76px] border-[3px] border-transparent border-t-blue-500 border-r-blue-500 rounded-full animate-spin" />
      </div>
      <p className="text-white text-sm font-semibold">Starting camera...</p>
      <p className="text-white/60 text-xs mt-2">Please wait...</p>
    </div>
  );

  const errorOverlay = error && (
    <div className="absolute inset-0 bg-black flex items-center justify-center z-10">
      <div className="text-center px-6">
        <IconCamera />
        <p className="text-white text-lg mb-2">Camera Error</p>
        <p className="text-gray-400 text-sm mb-4">{error}</p>
        <button onClick={() => { void haptics.warning(); startCamera(facingMode); }} className="px-6 py-2 bg-blue-600 text-white rounded-lg">
          Retry
        </button>
      </div>
    </div>
  );

  const focusRingElement = focusRing.visible && (
    <div
      className="absolute z-20 pointer-events-none"
      style={{
        left: focusRing.x - 30,
        top: focusRing.y - 30,
        width: 60,
        height: 60,
      }}
    >
      <div
        className="w-full h-full rounded-full border-2 border-yellow-400"
        style={{
          animation: 'cameraFocusRing 0.6s ease-out forwards',
        }}
      />
    </div>
  );

  // Zoom badge floating on the video
  const zoomBadge = zoomSupported && zoomLevel > zoomMin && (
    <div className="absolute top-3 left-1/2 -translate-x-1/2 z-20 bg-black/60 text-white text-xs font-mono px-2.5 py-1 rounded-full backdrop-blur-sm">
      {zoomText}
    </div>
  );

  // Sharpness quality indicator — shown when ML document detection is active.
  // Displays a small badge with a color-coded blur metric so the user knows
  // if the current framing is sharp enough before capturing.
  //  - Green  (sharpness >= 0.6): sharp
  //  - Yellow (0.3–0.6):          acceptable but could be sharper
  //  - Red    (< 0.3):            blurry — suggest moving closer or steadying
  const sharpnessBadge = mlEdgeWithCorners && mlEdgeWithCorners.sharpness > 0 && (
    <div
      className={`absolute top-3 right-3 z-20 flex items-center gap-1.5 px-2.5 py-1 rounded-full backdrop-blur-sm text-xs font-medium ${
        mlEdgeWithCorners.sharpness >= 0.6
          ? 'bg-green-500/30 text-green-200 border border-green-400/40'
          : mlEdgeWithCorners.sharpness >= 0.3
            ? 'bg-yellow-500/30 text-yellow-200 border border-yellow-400/40'
            : 'bg-red-500/30 text-red-200 border border-red-400/40'
      }`}
      title={`Sharpness: ${(mlEdgeWithCorners.sharpness * 100).toFixed(0)}%`}
    >
      <div
        className={`w-1.5 h-1.5 rounded-full ${
          mlEdgeWithCorners.sharpness >= 0.6
            ? 'bg-green-400'
            : mlEdgeWithCorners.sharpness >= 0.3
              ? 'bg-yellow-400'
              : 'bg-red-400 animate-pulse'
        }`}
      />
      {mlEdgeWithCorners.sharpness >= 0.6 ? 'Sharp' : mlEdgeWithCorners.sharpness >= 0.3 ? 'OK' : 'Blurry'}
    </div>
  );

  // Hide the floating AF/MF pill when the capture mode grid has its own focus button
  const gridHasFocusButton = !!(captureModeGrid && captureModeGrid.some(b => b.cameraAction === 'focusMode'));
  // Same for flash and auto-capture/auto-zoom — when the bottom grid
  // buttons control a camera action, hide the duplicate top/side toggles.
  // (The grid's "Auto" button is a master toggle for auto-capture AND
  // auto-zoom, so it suppresses both.)
  const gridHasFlashButton = !!(captureModeGrid && captureModeGrid.some(b => b.cameraAction === 'flash'));
  const gridHasAutoCaptureButton = !!(captureModeGrid && captureModeGrid.some(b => b.cameraAction === 'autoCapture'));

  // Autofocus indicator button — tap to toggle AF/MF
  const afIndicator = !gridHasFocusButton && (
    <button
      onClick={(e) => { e.stopPropagation(); void toggleFocusMode(); }}
      className={`absolute bottom-3 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2 px-3.5 py-1.5 rounded-full text-sm font-bold backdrop-blur-sm transition-all active:scale-95 ${autofocusActive ? 'bg-green-500/30 text-green-300 border border-green-400/50' : 'bg-yellow-500/30 text-yellow-300 border border-yellow-400/50'}`}
      aria-label={autofocusActive ? 'Switch to manual focus' : 'Switch to autofocus'}
      title={autofocusActive ? 'AF — tap for manual' : 'MF — tap for autofocus'}
    >
      <div className={`w-2 h-2 rounded-full ${autofocusActive ? 'bg-green-400' : 'bg-yellow-400 animate-pulse'}`} />
      {autofocusActive ? 'AF' : 'MF'}
    </button>
  );

  // Auto-capture countdown overlay — large number pulsing in the center
  const countdownOverlay = countdown !== null && (
    <div className="absolute inset-0 z-30 flex items-center justify-center pointer-events-none">
      <div className="relative">
        {/* Pulsing ring */}
        <div className="absolute inset-0 -m-8 rounded-full border-4 border-green-400/60 animate-ping" style={{ animationDuration: '1s' }} />
        <div className="absolute inset-0 -m-8 rounded-full border-4 border-green-400/40" />
        {/* Number — solid green circle like success toast */}
        <div className="w-20 h-20 rounded-full bg-green-500 border-2 border-green-300 flex items-center justify-center shadow-[0_0_30px_rgba(74,222,128,0.6)]">
          <span className="text-white text-5xl font-bold tabular-nums" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.5)' }}>
            {countdown}
          </span>
        </div>
      </div>
      {/* "Hold still" label */}
      <div className="absolute bottom-24 left-1/2 -translate-x-1/2 bg-black/60 backdrop-blur-sm rounded-full px-4 py-1.5">
        <span className="text-white text-sm font-medium">Hold steady — capturing…</span>
      </div>
    </div>
  );

  // Auto-capture toggle button — only shown in auto mode
  const autoCaptureToggle = isAutoMode && !gridHasAutoCaptureButton && (
    <button
      onClick={() => {
        const next = !autoCaptureOn;
        setAutoCaptureOn(next);
        try { localStorage.setItem(AUTO_CAPTURE_PREF_KEY, String(next)); } catch { /* ignore */ }
        setCountdown(null);
        stableSinceRef.current = null;
        edgeHistoryRef.current = [];
        autoCaptureTriggeredRef.current = false;
        if (countdownTimerRef.current) {
          clearInterval(countdownTimerRef.current);
          countdownTimerRef.current = null;
        }
      }}
      className={`p-2 rounded-full transition-colors ${autoCaptureOn ? 'bg-green-500/30 text-green-300' : 'text-white hover:bg-white/20'}`}
      aria-label={autoCaptureOn ? 'Disable auto-capture' : 'Enable auto-capture'}
      title={autoCaptureOn ? 'Auto-capture on' : 'Auto-capture off'}
    >
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    </button>
  );

  // Auto-zoom toggle button (compact) — for side panel and bottom controls
  const autoZoomToggle = isAutoMode && zoomSupported && !gridHasAutoCaptureButton && (
    <button
      onClick={() => {
        const next = !autoZoomOn;
        setAutoZoomOn(next);
        try { localStorage.setItem(AUTO_ZOOM_PREF_KEY, String(next)); } catch { /* ignore */ }
        // Reset auto-zoom state (also forces auto-capture to re-assess stability)
        settledSinceRef.current = null;
        noEdgeSinceRef.current = null;
        if (autoZoomRafRef.current) {
          cancelAnimationFrame(autoZoomRafRef.current);
          autoZoomRafRef.current = null;
        }
        // Reset auto-capture stability so it doesn't fire before zoom re-settles
        setCountdown(null);
        stableSinceRef.current = null;
        edgeHistoryRef.current = [];
        autoCaptureTriggeredRef.current = false;
        if (countdownTimerRef.current) {
          clearInterval(countdownTimerRef.current);
          countdownTimerRef.current = null;
        }
      }}
      className={`p-2 rounded-full transition-colors ${autoZoomOn ? 'bg-blue-500/30 text-blue-300' : 'text-white hover:bg-white/20'}`}
      aria-label={autoZoomOn ? 'Disable auto-zoom' : 'Enable auto-zoom'}
      title={autoZoomOn ? 'Auto-zoom on' : 'Auto-zoom off'}
    >
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7" />
      </svg>
    </button>
  );

  // Floating auto-capture toggle — always visible on the video area in auto mode
  const autoCaptureFloatingToggle = isAutoMode && !reviewImage && !gridHasAutoCaptureButton && (
    <button
      onClick={(e) => {
        e.stopPropagation();
        const next = !autoCaptureOn;
        setAutoCaptureOn(next);
        try { localStorage.setItem(AUTO_CAPTURE_PREF_KEY, String(next)); } catch { /* ignore */ }
        void haptics.selection();
        setCountdown(null);
        stableSinceRef.current = null;
        edgeHistoryRef.current = [];
        autoCaptureTriggeredRef.current = false;
        if (countdownTimerRef.current) {
          clearInterval(countdownTimerRef.current);
          countdownTimerRef.current = null;
        }
      }}
      className={`absolute top-3 right-3 z-20 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold backdrop-blur-sm transition-all active:scale-95 ${autoCaptureOn ? 'bg-green-500/30 text-green-300 border border-green-400/50' : 'bg-black/50 text-white/70 border border-white/20'}`}
      aria-label={autoCaptureOn ? 'Disable auto-capture' : 'Enable auto-capture'}
      title={autoCaptureOn ? 'Auto-capture on' : 'Auto-capture off'}
    >
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <span>Auto</span>
    </button>
  );

  // Floating auto-zoom toggle — shown in auto mode when zoom is supported
  const autoZoomFloatingToggle = isAutoMode && zoomSupported && !reviewImage && !gridHasAutoCaptureButton && (
    <button
      onClick={(e) => {
        e.stopPropagation();
        const next = !autoZoomOn;
        setAutoZoomOn(next);
        try { localStorage.setItem(AUTO_ZOOM_PREF_KEY, String(next)); } catch { /* ignore */ }
        void haptics.selection();
        // Reset auto-zoom state (also forces auto-capture to re-assess stability)
        settledSinceRef.current = null;
        noEdgeSinceRef.current = null;
        if (autoZoomRafRef.current) {
          cancelAnimationFrame(autoZoomRafRef.current);
          autoZoomRafRef.current = null;
        }
        // Reset auto-capture stability so it doesn't fire before zoom re-settles
        setCountdown(null);
        stableSinceRef.current = null;
        edgeHistoryRef.current = [];
        autoCaptureTriggeredRef.current = false;
        if (countdownTimerRef.current) {
          clearInterval(countdownTimerRef.current);
          countdownTimerRef.current = null;
        }
      }}
      className={`absolute top-3 right-20 z-20 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold backdrop-blur-sm transition-all active:scale-95 ${autoZoomOn ? 'bg-blue-500/30 text-blue-300 border border-blue-400/50' : 'bg-black/50 text-white/70 border border-white/20'}`}
      aria-label={autoZoomOn ? 'Disable auto-zoom' : 'Enable auto-zoom'}
      title={autoZoomOn ? 'Auto-zoom on' : 'Auto-zoom off'}
    >
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7" />
      </svg>
      <span>Zoom</span>
    </button>
  );

  // Whether the dynamic edge box is confident enough to display.
  // Threshold of 0.15 filters out marginal detections (uniform surfaces,
  // partial shadows) that cause the box to "hunt" when no document is in
  // frame. Real documents produce confidence well above this.
  // For vehicle mode, the threshold is lower (0.08) because vehicles are
  // not necessarily brighter than the background (unlike white paper).
  const dynamicEdgeThreshold = isVehicleMode ? 0.08 : 0.15;
  const hasDynamicEdge = (isAutoMode || isVehicleMode) && dynamicBoxPosition !== null && (displayEdge?.confidence ?? 0) > dynamicEdgeThreshold;

  // Whether the detected document fails the readability check (too far /
  // blurry / no resolvable text). Drives the orange bounding box and the
  // "move closer" guidance. Only evaluated when the gate is enabled.
  const docUnreadable = requireReadableDocument && isAutoMode && docReadability !== null && !docReadability.readable;

  // Bounding box color — orange when the document is detected but unreadable,
  // blue for a confident detection, amber while confidence is still marginal.
  const edgeBoxColor = docUnreadable
    ? 'rgba(251,146,60,0.95)'
    : displayEdge && displayEdge.confidence > 0.3
      ? '#2563eb'
      : 'rgba(255,200,50,0.85)';

  const guideOverlay = guide && (
    <>
      <div className="pointer-events-none absolute inset-0 z-20 flex items-start justify-center px-4 pt-4">
        <div className="max-w-sm rounded-2xl border border-white/10 bg-black/40 px-3 py-2 text-center text-white shadow-lg backdrop-blur-sm">
          {guide.title && <p className="text-sm font-semibold">{guide.title}</p>}
          {guide.subtitle && <p className="mt-1 text-[11px] leading-4 text-white/80">{guide.subtitle}</p>}
          {isAutoMode && (
            <p className="mt-1 text-[10px] leading-3 text-white/60">
              {countdown !== null
                ? 'Hold steady — auto-capturing…'
                : captureGateReason !== null
                  ? 'Tap again to capture anyway'
                  : docUnreadable
                    ? docReadability?.reason === 'blurry'
                      ? 'Hold steady — image blurry'
                      : 'Move closer — slip unreadable'
                    : autoCaptureOn && hasDynamicEdge
                      ? 'Document detected — hold steady'
                      : autoCaptureOn
                        ? 'Position receipt within frame'
                        : hasDynamicEdge
                          ? 'Document detected'
                          : 'Position receipt within frame'}
            </p>
          )}
          {isVehicleMode && (
            <p className="mt-1 text-[10px] leading-3 text-white/60">
              {countdown !== null
                ? 'Hold steady — auto-capturing…'
                : autoCaptureOn && hasDynamicEdge
                  ? 'Vehicle detected — hold steady'
                  : hasDynamicEdge
                    ? 'Vehicle detected — center it in frame'
                    : 'Position vehicle within the outline'}
            </p>
          )}
        </div>
      </div>

      {hasDynamicEdge ? (
        /* Dynamic bounding box — follows the detected edges (Dext-style) */
        <div className="pointer-events-none absolute inset-0 z-20">
          <div
            ref={guideFrameRef}
            className="absolute rounded-lg shadow-[0_0_0_9999px_rgba(0,0,0,0.22)] transition-all duration-100 ease-out"
            style={{
              ...dynamicBoxPosition!,
              // Blue matches the Smart Expense Capture icon (#3b82f6).
              // Uses #2563eb (blue-600, one shade darker) at full opacity so
              // the line reads clearly against bright receipts and the dark
              // vignette around the box. Orange when the detected document
              // isn't readable (too far away / blurry) — see edgeBoxColor.
              border: `2px solid ${edgeBoxColor}`,
            }}
          >
            {/* Corner markers — thicker L-shaped corners like Dext */}
            {[
              { className: 'top-0 left-0 border-t-[3px] border-l-[3px] rounded-tl' },
              { className: 'top-0 right-0 border-t-[3px] border-r-[3px] rounded-tr' },
              { className: 'bottom-0 left-0 border-b-[3px] border-l-[3px] rounded-bl' },
              { className: 'bottom-0 right-0 border-b-[3px] border-r-[3px] rounded-br' },
            ].map((corner, i) => (
              <div
                key={i}
                className={`absolute w-5 h-5 ${corner.className}`}
                style={{ borderColor: docUnreadable ? 'rgb(251,146,60)' : displayEdge && displayEdge.confidence > 0.3 ? '#2563eb' : 'rgb(255,200,50)' }}
              />
            ))}
            {/* Vehicle silhouette inside the dynamic box */}
            {isVehicleMode && (
              <div className="absolute inset-0 flex items-center justify-center p-2 text-white/30">
                <VehicleSilhouette profile={vehicleProfile} />
              </div>
            )}
          </div>
        </div>
      ) : (
        /* Static guide frame — fallback when no edge is detected or in manual mode */
        <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center px-4 py-6">
          <div
            ref={guideFrameRef}
            className="relative rounded-[28px] border-2 border-dashed border-white/85 bg-white/5 shadow-[0_0_0_9999px_rgba(0,0,0,0.18)]"
            style={getCameraGuideFrameStyle(guide.mode || 'auto', preferredOrientation === 'landscape' ? true : isLandscape)}
          >
            <div className="absolute inset-3 rounded-[22px] border border-white/25" />
            {(guide.mode === 'receipt' || guide.mode === 'continuous') && (
              <>
                <div className="absolute left-1/2 top-3 h-2 w-px -translate-x-1/2 bg-white/60" />
                <div className="absolute left-1/2 bottom-3 h-2 w-px -translate-x-1/2 bg-white/60" />
              </>
            )}
            {guide.mode === 'continuous' && (
              <>
                <div className="absolute left-4 right-4 top-1/3 border-t border-dashed border-white/35" />
                <div className="absolute left-4 right-4 top-2/3 border-t border-dashed border-white/35" />
              </>
            )}
            {/* Vehicle silhouette inside the static frame */}
            {isVehicleMode && (
              <div className="absolute inset-0 flex items-center justify-center p-3 text-white/30">
                <VehicleSilhouette profile={vehicleProfile} />
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );

  // Low-light warning banner (dark mode enhancement)
  // Shows when in low light and the torch isn't on — either mode is 'off', or
  // mode is 'auto' but the hysteresis hasn't turned the torch on yet.
  // Low-light / torch-enable prompt.
  // In a WebView (Capacitor), applyConstraints({ torch: true }) requires a
  // user gesture — like audio autoplay. Effect-initiated torch calls fail
  // silently on camera open. So when torchMode is 'on' or 'auto' and the torch
  // isn't on yet, show a tappable prompt. The user's tap provides the gesture
  // needed to enable the torch; after that, auto mode manages it normally.
  const showTorchPrompt = !reviewImage && !torchEnabled && torchSupported && (
    torchMode === 'on' || (torchMode === 'auto' && isLowLight)
  );

  // Rotate-to-landscape hint — shown when landscape is preferred but the phone
  // is in portrait. Non-blocking: the user can still capture in portrait.
  const rotateHint = showRotateHint && (
    <div className="pointer-events-none absolute inset-x-0 top-1/2 z-30 flex -translate-y-1/2 justify-center">
      <div className="flex flex-col items-center gap-2 rounded-2xl bg-black/60 px-5 py-4 backdrop-blur-sm">
        <svg className="h-10 w-10 text-white/90 animate-pulse" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 7.5h18M21 7.5l-3-3M21 7.5l-3 3M21 16.5H3M3 16.5l3-3M3 16.5l3 3" />
        </svg>
        <p className="text-sm font-semibold text-white">Rotate to landscape</p>
        <p className="text-[11px] text-white/70">Hold phone sideways for the best photo</p>
      </div>
    </div>
  );
  const lowLightBanner = showTorchPrompt && (
    <div className="absolute inset-x-0 bottom-28 z-30 flex justify-center px-4">
      <button
        onClick={() => {
          // Turn torch on directly — this tap is the user gesture that
          // allows applyConstraints({ torch: true }) to work.
          if (!torchEnabledRef.current) {
            autoTorchPendingRef.current = true;
            void toggleTorchRef.current().finally(() => { autoTorchPendingRef.current = false; });
            void haptics.toggle();
          }
        }}
        className="flex items-center gap-2 rounded-full bg-amber-500/90 px-4 py-2 text-sm font-medium text-black shadow-lg active:scale-95 transition-transform animate-pulse"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M13 2L4.5 13.5H11L10 22L20.5 10.5H14L13 2Z" /></svg>
        <span>Tap to enable flash</span>
      </button>
    </div>
  );

  // Zoom slider row (horizontal for portrait, vertical for landscape)
  const zoomSlider = zoomSupported && (
    <div className={`flex items-center gap-2 ${isLandscape ? 'flex-col-reverse px-1 py-2' : 'px-4 py-1.5'}`}>
      <button
        onClick={() => { disableAutoZoom(); void setZoom(Math.max(zoomLevel - 0.5, zoomMin)); }}
        className="p-1 text-white/70 hover:text-white active:scale-90 transition-all"
        aria-label="Zoom out"
      >
        <IconZoomOut />
      </button>
      <input
        type="range"
        min={zoomMin}
        max={zoomMax}
        step={0.1}
        value={zoomLevel}
        onPointerDown={disableAutoZoom}
        onTouchStart={disableAutoZoom}
        onChange={e => { disableAutoZoom(); void setZoom(parseFloat(e.target.value)); }}
        className={`accent-white/80 ${isLandscape ? 'h-24 writing-vertical' : 'flex-1'}`}
        style={isLandscape ? {
          writingMode: 'vertical-lr',
          direction: 'rtl',
          WebkitAppearance: 'slider-vertical' as CSSProperties['WebkitAppearance'],
          width: 20,
          height: 96,
        } satisfies CSSProperties : undefined}
        aria-label="Zoom"
      />
      <button
        onClick={() => { disableAutoZoom(); void setZoom(Math.min(zoomLevel + 0.5, zoomMax)); }}
        className="p-1 text-white/70 hover:text-white active:scale-90 transition-all"
        aria-label="Zoom in"
      >
        <IconZoomIn />
      </button>
    </div>
  );

  // ---- Contextual Shortcuts Bar ----
  // Quick-action pills shown above the shutter button, context-aware.
  // Hidden during review overlay.
  const shortcutsBar = !captureModeGrid && shortcuts.length > 0 && !reviewImage && (
    <div className={`flex items-center gap-1.5 overflow-x-auto ${isLandscape ? 'flex-col max-h-32' : 'px-4 pb-1'}`}>
      {shortcuts.map((sc) => (
        <button
          key={sc.key}
          onClick={(e) => {
            e.stopPropagation();
            sc.onClick();
          }}
          className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium whitespace-nowrap transition-all active:scale-95 ${
            sc.active
              ? 'bg-white/90 text-black shadow-lg'
              : 'bg-white/15 text-white backdrop-blur-sm hover:bg-white/25'
          }`}
        >
          {sc.icon && <span className="text-sm leading-none">{sc.icon}</span>}
          <span>{sc.label}</span>
        </button>
      ))}
    </div>
  );

  // ---- Capture Mode Grid (2×2) ----
  // Replaces the shortcuts bar + shutter with a 2×2 grid of capture buttons.
  // The button with isCapture=true is placed bottom-right as the generic capture.
  // Floating Done button — shown when batch mode has at least 1 photo.
  // Rendered as a separate bar between zoom slider and bottom controls for thumb reach.
  const floatingDoneButton = isBatchMode && batchPhotos.length > 0 && !reviewImage && (
    <div
      className="flex justify-center items-center py-2 bg-black/80"
      style={{ flexShrink: 0 }}
    >
      <button
        onClick={handleDoneBatch}
        className="px-8 py-2.5 rounded-full bg-green-500 text-white text-sm font-bold active:scale-95 transition-transform shadow-lg z-30"
      >
        Done ({batchPhotos.length})
      </button>
    </div>
  );

  const captureModeGridUI = captureModeGrid && captureModeGrid.length > 0 && !reviewImage && (
    <div
      className={`grid grid-cols-2 ${isLandscape ? 'gap-2 px-2' : 'gap-2.5 px-6'}`}
      style={{ paddingBottom: isLandscape ? '4px' : 'max(24px, calc(env(safe-area-inset-bottom) + 16px))' }}
    >
      {captureModeGrid.map((btn) => {
        const isCamAction = !!btn.cameraAction;
        const camActive = btn.cameraAction === 'flash'
          ? torchMode !== 'off'
          : btn.cameraAction === 'focusMode'
            ? autofocusActive
            : btn.cameraAction === 'autoCapture'
              ? autoCaptureOn
              : false;
        const camDisabled = btn.cameraAction === 'flash'
          ? isTorchChecking
          : btn.cameraAction === 'autoCapture'
            ? !autoCaptureEnabled
            : false;
        const displayLabel = btn.cameraAction === 'flash'
          ? (torchMode === 'auto' ? 'Flash Auto' : torchMode === 'on' ? 'Flash On' : 'Flash')
          : btn.cameraAction === 'focusMode'
            ? (autofocusActive ? 'Auto Focus' : 'Manual Focus')
            : btn.cameraAction === 'autoCapture'
              ? (autoCaptureOn ? 'Auto On' : 'Auto')
              : btn.label;
        const displayIcon = btn.cameraAction === 'flash'
          ? (torchMode === 'auto' ? '🔆' : torchMode === 'on' ? '⚡' : '🔦')
          : btn.cameraAction === 'focusMode'
            ? (autofocusActive ? '🎯' : '🔧')
            : btn.cameraAction === 'autoCapture'
              ? (autoCaptureOn ? '📸' : '📷')
              : btn.icon;
        // Flash button uses amber (auto) / yellow (on) to match the regular
        // flash toggle; other camera actions use green when active.
        const camActiveClass = btn.cameraAction === 'flash'
          ? (torchMode === 'auto' ? 'bg-amber-500/30 text-amber-200 border border-amber-400/50' : 'bg-yellow-500/30 text-yellow-200 border border-yellow-400/50')
          : 'bg-green-500/30 text-green-200 backdrop-blur-sm border border-green-400/50';

        return (
          <button
            key={btn.key}
            onClick={(e) => {
              e.stopPropagation();
              if (btn.cameraAction === 'flash') {
                void handleTorchToggle();
              } else if (btn.cameraAction === 'focusMode') {
                void haptics.toggle();
                void toggleFocusMode();
              } else if (btn.cameraAction === 'autoCapture') {
                const next = !autoCaptureOn;
                setAutoCaptureOn(next);
                try { localStorage.setItem(AUTO_CAPTURE_PREF_KEY, String(next)); } catch { /* ignore */ }
                // Also toggle auto-zoom — the "Auto" button is the user's
                // mental model for all automatic camera behaviors. Without
                // this, turning "Auto" off leaves auto-zoom running (the
                // camera keeps zooming in/out), which is the most visible
                // automatic behavior. Sync both so the button is a true
                // master toggle for automatic capture + zoom.
                setAutoZoomOn(next);
                try { localStorage.setItem(AUTO_ZOOM_PREF_KEY, String(next)); } catch { /* ignore */ }
                void haptics.selection();
                setCountdown(null);
                stableSinceRef.current = null;
                edgeHistoryRef.current = [];
                autoCaptureTriggeredRef.current = false;
                if (countdownTimerRef.current) {
                  clearInterval(countdownTimerRef.current);
                  countdownTimerRef.current = null;
                }
                // Reset auto-zoom animation state so it stops immediately
                settledSinceRef.current = null;
                noEdgeSinceRef.current = null;
                if (autoZoomRafRef.current) {
                  cancelAnimationFrame(autoZoomRafRef.current);
                  autoZoomRafRef.current = null;
                }
              } else if (btn.skipCapture) {
                btn.onClick();
                void haptics.light();
              } else {
                handleModeCapture(btn);
              }
            }}
            disabled={!!error || isLoading || camDisabled}
            className={`flex flex-col items-center justify-center gap-1.5 rounded-2xl ${isLandscape ? 'py-3' : 'py-4'} transition-all active:scale-95 disabled:opacity-50 ${
              btn.isCapture
                ? 'bg-white text-black shadow-lg col-start-2 row-start-2'
                : isCamAction && camActive
                  ? camActiveClass
                  : 'bg-white/15 text-white backdrop-blur-sm hover:bg-white/25'
            }`}
          >
            {displayIcon && <span className="text-xl leading-none">{displayIcon}</span>}
            <span className={`text-xs ${btn.isCapture ? 'font-bold' : 'font-medium'}`}>{displayLabel}</span>
          </button>
        );
      })}
    </div>
  );

  // Shutter button
  const batchAtMax = isBatchMode && batchPhotos.length >= batchMaxPhotos;
  const shutterButton = (
    <button
      onClick={handleCapture}
      disabled={!!error || isLoading || batchAtMax}
      className="w-[72px] h-[72px] rounded-full bg-white border-4 border-gray-300 flex items-center justify-center active:scale-95 transition-transform disabled:opacity-50 shadow-lg"
      aria-label="Capture photo"
    >
      <div className="w-[60px] h-[60px] rounded-full bg-white border-2 border-gray-200" />
    </button>
  );

  // ---- Capture Review Overlay ----
  // Full-screen preview of the captured photo with Use/Retake buttons.
  // When perspectiveCorrection is enabled, shows draggable corner handles
  // and Apply/Reset buttons for document flattening.
  const reviewOverlay = reviewImage && (
    <div className="absolute inset-0 z-40 bg-black flex flex-col">
      {/* Preview image with optional perspective corner overlay */}
      <div className="flex-1 flex items-center justify-center overflow-hidden min-h-0 relative" ref={reviewContainerRef}>
        <img
          ref={reviewImageRef}
          src={reviewImage}
          alt="Captured photo"
          className="max-w-full max-h-full object-contain"
          draggable={false}
        />
        {/* Batch thumbnails — show previously captured pages alongside the
            current review photo so the user can see all pages of this expense. */}
        {batchPhotos.length > 0 && (
          <div className="absolute top-0 left-0 right-0 z-10 bg-black/60 backdrop-blur-sm" style={{ paddingTop: 'max(8px, env(safe-area-inset-top))' }}>
            <div className="flex items-center gap-2 px-3 py-2 overflow-x-auto">
              <span className="text-[10px] text-white/60 font-medium whitespace-nowrap flex-shrink-0">
                {batchPhotos.length + 1} page{batchPhotos.length + 1 > 1 ? 's' : ''}
              </span>
              {batchPhotos.map((photo, idx) => (
                <div key={idx} className="relative flex-shrink-0 w-12 h-16 rounded overflow-hidden border border-white/30">
                  <img src={photo} alt={`Page ${idx + 1}`} className="w-full h-full object-cover" />
                  <span className="absolute bottom-0 left-0 right-0 text-[8px] text-white bg-black/60 text-center">{idx + 1}</span>
                </div>
              ))}
              <div className="relative flex-shrink-0 w-12 h-16 rounded overflow-hidden border-2 border-blue-400">
                <img src={reviewImage} alt="Current" className="w-full h-full object-cover" />
                <span className="absolute bottom-0 left-0 right-0 text-[8px] text-blue-200 bg-blue-900/60 text-center">{batchPhotos.length + 1}</span>
              </div>
            </div>
          </div>
        )}
        {/* Partial document indicator — when ML detected fewer than 4
            corners, the document extends beyond the frame. Show a banner
            so the user knows they captured only part of the document. */}
        {reviewCaptureMeta.cornerCount > 0 && !reviewCaptureMeta.allCornersDetected && documentProfile !== 'till-slip' && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 bg-amber-500/20 border border-amber-400/50 rounded-full px-4 py-2 backdrop-blur-sm">
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-amber-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-amber-200 text-xs font-medium">
                {reviewCaptureMeta.documentType === 'till-slip'
                  ? 'Partial slip — capture the bottom too'
                  : 'Partial document — some corners may be cut off'}
              </span>
            </div>
          </div>
        )}
        {/* For till-slip profiles with partial detection, show a helpful
            guidance message. The message adapts to the available buttons:
            - Multi-page possible (batch mode / partial detection): Cancel + Add Another + Done
              → "tap Add Page for the next section or Done to finish"
            - Single-page known (batch_capture=false, e.g. fuel/toll slips):
              Cancel + Done → "tap Done to finish" (no Add Page button shown) */}
        {documentProfile === 'till-slip' && !reviewCaptureMeta.allCornersDetected && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 bg-blue-500/20 border border-blue-400/50 rounded-full px-4 py-2 backdrop-blur-sm">
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-blue-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-blue-200 text-xs font-medium">
                {multiPagePossible
                  ? 'Segment captured — tap Add Page for the next section or Done to finish'
                  : 'Segment captured — tap Done to finish'}
              </span>
            </div>
          </div>
        )}
        {/* Perspective corner handles — only before correction is applied.
            Wrapped in a div that matches the image's rendered position so
            that percentage-based coordinates are relative to the image, not
            the letterboxed container. */}
        {perspectiveCorrection && perspectiveCorners && !perspectiveApplied && reviewImgLayout && (
          <div
            className="absolute pointer-events-none"
            style={{
              left: reviewImgLayout.left,
              top: reviewImgLayout.top,
              width: reviewImgLayout.width,
              height: reviewImgLayout.height,
            }}
          >
          <svg
            className="w-full h-full pointer-events-none"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
          >
            {/* Corner connecting lines */}
            <polygon
              points={`${perspectiveCorners[0].x * 100},${perspectiveCorners[0].y * 100} ${perspectiveCorners[1].x * 100},${perspectiveCorners[1].y * 100} ${perspectiveCorners[2].x * 100},${perspectiveCorners[2].y * 100} ${perspectiveCorners[3].x * 100},${perspectiveCorners[3].y * 100}`}
              fill="rgba(59,130,246,0.08)"
              stroke="rgba(59,130,246,0.8)"
              strokeWidth="0.4"
              strokeDasharray="1.5,1"
            />
          </svg>
          {perspectiveCorners.map((corner, idx) => (
          <div
            key={idx}
            className="absolute pointer-events-auto"
            style={{
              left: `${corner.x * 100}%`,
              top: `${corner.y * 100}%`,
              transform: 'translate(-50%, -50%)',
              touchAction: 'none',
            }}
            onPointerDown={(e) => {
              e.preventDefault();
              (e.target as HTMLElement).setPointerCapture(e.pointerId);
              draggingCornerRef.current = idx;
            }}
            onPointerMove={(e) => {
              if (draggingCornerRef.current === idx) {
                handleCornerDrag(idx, e.clientX, e.clientY);
              }
            }}
            onPointerUp={(e) => {
              (e.target as HTMLElement).releasePointerCapture(e.pointerId);
              draggingCornerRef.current = null;
            }}
          >
            <div className="w-8 h-8 rounded-full border-2 border-blue-400 bg-blue-400/30 backdrop-blur-sm flex items-center justify-center shadow-lg cursor-grab active:cursor-grabbing active:scale-110 transition-transform">
              <div className="w-2.5 h-2.5 rounded-full bg-blue-400" />
            </div>
          </div>
          ))}
          </div>
        )}
        {/* Applying overlay */}
        {perspectiveApplying && (
          <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
            <div className="flex flex-col items-center gap-3">
              <div className="w-10 h-10 border-3 border-white/30 border-t-white rounded-full animate-spin" />
              <span className="text-white/80 text-sm font-medium">Flattening…</span>
            </div>
          </div>
        )}
      </div>
      {/* Expense categorization bar — shown above the action buttons on the
          review screen so the user can confirm they're submitting the right
          slip for the right expense. Supports multi-line via \n. */}
      {expenseContext && (
        <div
          className="bg-black/90 flex items-center justify-center px-4 py-2"
          style={{ flexShrink: 0 }}
        >
          <p className="text-white/80 text-xs sm:text-sm font-medium text-center whitespace-pre-line leading-relaxed">
            {expenseContext}
          </p>
        </div>
      )}
      {/* Action buttons */}
      <div
        className="flex items-center justify-center gap-6 bg-black/90"
        style={{
          flexShrink: 0,
          paddingTop: '16px',
          paddingBottom: 'max(32px, calc(env(safe-area-inset-bottom) + 28px))',
        }}
      >
        {reviewButtons === 'doneOnly' ? (
          /* Done only — for single-page flows where retake is not needed
             (e.g. casual wage authorization photo). No cancel, no add page. */
          <div className="flex items-center justify-center gap-6">
            <button
              onClick={handleDoneReview}
              className="flex flex-col items-center gap-1 text-green-300 hover:text-green-200 transition-colors"
              aria-label="Done — submit photo"
            >
              <div className="w-16 h-16 rounded-full bg-green-500/20 border-2 border-green-400 flex items-center justify-center active:scale-90 transition-transform shadow-[0_0_20px_rgba(74,222,128,0.3)]">
                <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <span className="text-sm font-semibold">Done</span>
            </button>
          </div>
        ) : reviewButtons === 'captureAnotherDone' ? (
          /* Batch-aware flow — one screen, no second prompt.
             - Multi-page possible (partial till-slip or batch mode):
               Cancel + Add Another + Use Photo (3 buttons)
             - Single-page known (all corners detected, non-batch):
               Cancel + Use Photo (2 buttons)
             Cancel = discard current photo, return to live camera.
             Use Photo = submit all photos (batch + current), close camera.
             Add Another = save current photo to batch, return to live camera. */
          <>
            {multiPagePossible ? (
              <div className="flex items-center justify-center gap-6">
                {/* Cancel — discard current photo, return to live camera */}
                <button
                  onClick={handleRetake}
                  className="flex flex-col items-center gap-1 text-white/80 hover:text-white transition-colors"
                  aria-label="Cancel and retake"
                >
                  <div className="w-14 h-14 rounded-full bg-white/10 flex items-center justify-center active:scale-90 transition-transform">
                    <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </div>
                  <span className="text-xs font-medium">Cancel</span>
                </button>
                {/* Add Another — save current photo to batch, return to live camera */}
                <button
                  onClick={handleCaptureAnother}
                  className="flex flex-col items-center gap-1 text-blue-300 hover:text-blue-200 transition-colors"
                  aria-label="Add another photo"
                >
                  <div className="w-16 h-16 rounded-full bg-blue-500/20 border-2 border-blue-400 flex items-center justify-center active:scale-90 transition-transform shadow-[0_0_20px_rgba(96,165,250,0.3)]">
                    <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                    </svg>
                  </div>
                  <span className="text-sm font-semibold">Add Another</span>
                </button>
                {/* Use Photo — submit all photos (batch + current), close camera */}
                <button
                  onClick={handleDoneReview}
                  className="flex flex-col items-center gap-1 text-green-300 hover:text-green-200 transition-colors"
                  aria-label="Use photo — submit and continue"
                >
                  <div className="w-16 h-16 rounded-full bg-green-500/20 border-2 border-green-400 flex items-center justify-center active:scale-90 transition-transform shadow-[0_0_20px_rgba(74,222,128,0.3)]">
                    <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-sm font-semibold">Use Photo</span>
                </button>
              </div>
            ) : (
              /* Single-page known — Cancel + Done.
                 Fuel/toll slips (batch_capture=false) are always single-page,
                 so the confirm button is labelled "Done" to match the overlay
                 guidance text ("tap Done to finish"). */
              <div className="flex items-center justify-center gap-6">
                {/* Cancel — discard current photo, return to live camera */}
                <button
                  onClick={handleRetake}
                  className="flex flex-col items-center gap-1 text-white/80 hover:text-white transition-colors"
                  aria-label="Cancel and retake"
                >
                  <div className="w-14 h-14 rounded-full bg-white/10 flex items-center justify-center active:scale-90 transition-transform">
                    <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </div>
                  <span className="text-xs font-medium">Cancel</span>
                </button>
                {/* Done — submit photo, close camera */}
                <button
                  onClick={handleDoneReview}
                  className="flex flex-col items-center gap-1 text-green-300 hover:text-green-200 transition-colors"
                  aria-label="Done — submit photo"
                >
                  <div className="w-16 h-16 rounded-full bg-green-500/20 border-2 border-green-400 flex items-center justify-center active:scale-90 transition-transform shadow-[0_0_20px_rgba(74,222,128,0.3)]">
                    <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-sm font-semibold">Done</span>
                </button>
              </div>
            )}
          </>
        ) : (
          <>
            <button
              onClick={handleRetake}
              className="flex flex-col items-center gap-1 text-white/80 hover:text-white transition-colors"
              aria-label="Retake photo"
            >
              <div className="w-14 h-14 rounded-full bg-white/10 flex items-center justify-center active:scale-90 transition-transform">
                <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </div>
              <span className="text-xs font-medium">Retake</span>
            </button>
            {/* Cancel — discard photo and close camera entirely */}
            <button
              onClick={handleCancelReview}
              className="flex flex-col items-center gap-1 text-red-300 hover:text-red-200 transition-colors"
              aria-label="Cancel and close camera"
            >
              <div className="w-14 h-14 rounded-full bg-red-500/15 border-2 border-red-400/50 flex items-center justify-center active:scale-90 transition-transform">
                <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <span className="text-xs font-medium">Cancel</span>
            </button>
            {/* Perspective correction buttons */}
            {perspectiveCorrection && !perspectiveApplied && (
              <button
                onClick={handleApplyPerspective}
                disabled={perspectiveApplying}
                className="flex flex-col items-center gap-1 text-blue-300 hover:text-blue-200 transition-colors disabled:opacity-50"
                aria-label="Apply perspective correction"
              >
                <div className="w-14 h-14 rounded-full bg-blue-500/20 border-2 border-blue-400 flex items-center justify-center active:scale-90 transition-transform">
                  <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 7l3-3 3 3M3 17l3 3 3-3M21 7l-3-3-3 3M21 17l-3 3-3-3M7 4h10M7 20h10M4 7v10M20 7v10" />
                  </svg>
                </div>
                <span className="text-xs font-medium">Flatten</span>
              </button>
            )}
            {perspectiveCorrection && perspectiveApplied && (
              <button
                onClick={handleResetPerspective}
                className="flex flex-col items-center gap-1 text-amber-300 hover:text-amber-200 transition-colors"
                aria-label="Reset perspective correction"
              >
                <div className="w-14 h-14 rounded-full bg-amber-500/20 border-2 border-amber-400 flex items-center justify-center active:scale-90 transition-transform">
                  <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 12a9 9 0 1 0 9-9M3 12V3m0 9h9" />
                  </svg>
                </div>
                <span className="text-xs font-medium">Undo</span>
              </button>
            )}
            <button
              onClick={handleUsePhoto}
              className="flex flex-col items-center gap-1 text-green-300 hover:text-green-200 transition-colors"
              aria-label="Use photo"
            >
              <div className="w-16 h-16 rounded-full bg-green-500/20 border-2 border-green-400 flex items-center justify-center active:scale-90 transition-transform shadow-[0_0_20px_rgba(74,222,128,0.3)]">
                <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <span className="text-sm font-semibold">Use Photo</span>
            </button>
          </>
        )}
      </div>
    </div>
  );

  // ---- Capture Mode Selector ----
  // Segmented control for switching between Single / Combine / Multiple modes.
  // Only shown when captureMode prop is provided.
  // Rendered as a top bar in portrait (very top of the modal, before the header)
  // and as an absolute overlay in landscape (top of the video area).
  const modeSelectorVisible = showModeSelector && !reviewImage && batchPhotos.length === 0;
  const modeSelectorContent = modeSelectorVisible && (
    <div className="flex items-center gap-1 px-3 py-2">
      {(['single', 'combine', 'multiple'] as const).map((mode) => (
        <button
          key={mode}
          type="button"
          onClick={() => { setCaptureModeState(mode); void haptics.selection(); }}
          className={`rounded-full px-3 py-1 text-[11px] font-semibold transition-all ${
            captureModeState === mode
              ? 'bg-white text-black shadow-sm'
              : 'text-white/70 hover:text-white'
          }`}
        >
          {mode === 'single' ? 'Single' : mode === 'combine' ? 'Combine' : 'Multiple'}
        </button>
      ))}
    </div>
  );
  // Portrait: regular flex child at the very top of the modal (handles safe-area)
  const modeSelectorBar = modeSelectorContent && (
    <div
      className="flex justify-center bg-black/80"
      style={{ flexShrink: 0, paddingTop: 'max(6px, env(safe-area-inset-top))' }}
    >
      {modeSelectorContent}
    </div>
  );
  // Landscape: absolute overlay at the top of the video area
  const modeSelectorOverlay = modeSelectorContent && (
    <div className="absolute top-0 left-0 right-0 z-30 flex justify-center bg-black/70 backdrop-blur-sm">
      {modeSelectorContent}
    </div>
  );

  // ---- Batch Thumbnail Strip ----
  // Shows captured photos as small thumbnails with a Done button.
  // Appears at the top of the camera view when batch mode has photos.
  const batchStrip = isBatchMode && batchPhotos.length > 0 && !reviewImage && (
    <div className="absolute top-0 left-0 right-0 z-30 bg-black/70 backdrop-blur-sm" style={{ paddingTop: 'max(8px, env(safe-area-inset-top))' }}>
      <div className="flex items-center gap-2 px-3 py-2 overflow-x-auto">
        {/* Thumbnail count badge */}
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-green-500/30 border border-green-400/50 flex items-center justify-center">
          <span className="text-green-300 text-xs font-bold">{batchPhotos.length}</span>
        </div>
        {/* Thumbnails */}
        <div className="flex items-center gap-1.5 flex-1 overflow-x-auto">
          {batchPhotos.map((photo, idx) => (
            <div key={idx} className="relative flex-shrink-0 group">
              <img
                src={photo}
                alt={`Page ${idx + 1}`}
                className="w-10 h-12 object-cover rounded border border-white/20"
              />
              <button
                onClick={() => handleRemoveBatchPhoto(idx)}
                className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500 flex items-center justify-center text-white text-[8px] font-bold opacity-80 hover:opacity-100"
                aria-label={`Remove page ${idx + 1}`}
              >
                ×
              </button>
              <span className="absolute bottom-0 left-0 right-0 text-center text-[7px] text-white/80 bg-black/50">{idx + 1}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // Context photos strip — read-only reference of already-captured pages.
  // Shown when the camera is reopened via "Add Page" so the user can see
  // existing pages and overlap correctly with the last one.
  const contextStrip = contextPhotos.length > 0 && batchPhotos.length === 0 && !reviewImage && (
    <div className="absolute top-0 left-0 right-0 z-30 bg-black/70 backdrop-blur-sm" style={{ paddingTop: 'max(8px, env(safe-area-inset-top))' }}>
      <div className="flex items-center gap-2 px-3 py-2 overflow-x-auto">
        <div className="flex-shrink-0 text-[10px] text-white/60 font-medium whitespace-nowrap">
          Captured:
        </div>
        <div className="flex items-center gap-1.5 flex-1 overflow-x-auto">
          {contextPhotos.map((photo, idx) => (
            <div key={idx} className="relative flex-shrink-0">
              <img
                src={photo}
                alt={`Page ${idx + 1}`}
                className="w-10 h-12 object-cover rounded border border-white/20 opacity-80"
              />
              <span className="absolute bottom-0 left-0 right-0 text-center text-[7px] text-white/80 bg-black/50">{idx + 1}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // ---- LANDSCAPE LAYOUT ----
  if (isLandscape) {
    const landscapeModal = (
      <div
        className={`fixed inset-0 bg-black ${modalZIndexClassName}`}
        style={{
          display: 'flex',
          flexDirection: 'row',
          height: '100dvh',
          minHeight: '100dvh',
          maxHeight: '100dvh',
        }}
      >
        {/* Video area — takes up most of the width */}
        <div
          ref={videoContainerRef}
          className="relative bg-black overflow-hidden"
          style={{
            flex: '1 1 0%',
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            paddingLeft: 'env(safe-area-inset-left)',
          }}
          onClick={handleVideoTap}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {videoElement}
          {loadingOverlay}
          {errorOverlay}
          {focusRingElement}
          {zoomBadge}
          {sharpnessBadge}
          {guideOverlay}
          {rotateHint}
          {lowLightBanner}
          {autoCaptureFloatingToggle}
          {autoZoomFloatingToggle}
          {afIndicator}
          {countdownOverlay}
          {batchStrip}
          {contextStrip}
          {modeSelectorOverlay}
        </div>

        {/* Side control panel — shutter pinned at the bottom so it never
            scrolls out of view on short landscape screens; the middle controls
            scroll instead. When the 2×2 capture-mode grid is present the panel
            widens so all four buttons stay thumb-reachable on the right edge. */}
        <div
          className="bg-black/90 flex flex-col items-center overflow-hidden"
          style={{
            flexShrink: 0,
            width: captureModeGrid && captureModeGrid.length > 0
              ? 'max(196px, calc(env(safe-area-inset-right) + 188px))'
              : 'max(100px, calc(env(safe-area-inset-right) + 92px))',
            paddingTop: 'max(8px, env(safe-area-inset-top))',
            paddingBottom: 'max(12px, env(safe-area-inset-bottom))',
            paddingRight: 'env(safe-area-inset-right)',
            paddingLeft: '4px',
          }}
        >
          {/* Top: close + title */}
          <div className="flex flex-col items-center gap-1 pt-1 flex-shrink-0">
            <button
              onClick={() => { void haptics.modalClose(); close(); }}
              className="p-2 text-white hover:bg-white/20 rounded-full transition-colors"
              aria-label="Close camera"
            >
              <IconClose />
            </button>
            <span className="text-white/60 text-[10px] font-medium tracking-wide uppercase">{title}</span>
          </div>

          {/* Middle: scrollable controls */}
          <div className="flex-1 min-h-0 w-full overflow-y-auto flex flex-col items-center justify-start gap-3 py-2">
            <div className="flex flex-col items-center gap-3">
              {shouldShowTorchControl && !gridHasFlashButton && (
                <div className="flex flex-col items-center gap-1">
                  <CameraActionButton
                    onClick={handleTorchToggle}
                    active={torchMode !== 'off'}
                    activeClass={torchMode === 'auto' ? 'bg-amber-500/20 text-amber-300' : 'bg-yellow-500/30 text-yellow-300'}
                    label={`Flash: ${torchMode}`}
                  >
                    <TorchIcon mode={torchMode} />
                  </CameraActionButton>
                </div>
              )}
              {shouldShowSwitchCameraControl && (
                <CameraActionButton onClick={switchCamera} disabled={!canSwitchCamera && availableCameraCount > 0} label={switchCameraLabel}>
                  <IconSwitchCamera />
                </CameraActionButton>
              )}
              {/* Tap-to-focus hint button (triggers center focus) */}
              <CameraActionButton onClick={() => tapToFocus(0.5, 0.5)} active={!autofocusActive} activeClass="bg-yellow-500/30 text-yellow-300" label="Focus center">
                <IconFocus />
              </CameraActionButton>
              {/* Auto-capture toggle */}
              {autoCaptureToggle}
              {/* Auto-zoom toggle */}
              {autoZoomToggle}
            </div>

            {/* Zoom slider (vertical) */}
            {zoomSlider}

            {/* Floating Done button — above capture controls for thumb reach */}
            {floatingDoneButton}

            {/* Contextual shortcuts */}
            {shortcutsBar}
          </div>

          {/* Bottom: capture control — pinned, always reachable */}
          {captureModeGridUI ? (
            <div className="flex-shrink-0 w-full">
              {captureModeGridUI}
            </div>
          ) : (
            <div className="pb-2 pt-1 flex-shrink-0">
              {captureButton || shutterButton}
            </div>
          )}
        </div>

        {/* Focus ring keyframe animation */}
        <style>{focusRingKeyframes}</style>
        {reviewOverlay}
      </div>
    );

    return typeof document !== 'undefined'
      ? createPortal(landscapeModal, document.body)
      : null;
  }

  // ---- PORTRAIT LAYOUT ----
  const portraitModal = (
    <div
      className={`fixed inset-0 bg-black ${modalZIndexClassName}`}
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100dvh',
        minHeight: '100dvh',
        maxHeight: '100dvh',
      }}
    >
      {/* Capture mode selector — very top of the modal (before header) */}
      {modeSelectorBar}

      {/* Header bar — title left, flash / switch / close right */}
      <div
        className="flex items-center gap-2 px-4 py-2 bg-black/80"
        style={{
          flexShrink: 0,
          // When the mode selector bar is shown it already handles the safe-area
          // inset, so the header only needs a small top padding.
          paddingTop: modeSelectorBar ? '4px' : 'max(10px, calc(env(safe-area-inset-top) + 4px))',
        }}
      >
        <span className="text-white font-medium text-sm flex-1 min-w-0 truncate">{title}</span>
        <div className="flex items-center gap-1 flex-shrink-0">
          {shouldShowTorchControl && !gridHasFlashButton && (
            <>
              <button
                onClick={handleTorchToggle}
                className={`p-2 rounded-full transition-colors ${
                  torchMode === 'on' ? 'bg-yellow-500/30 text-yellow-300'
                  : torchMode === 'auto' ? 'text-amber-300 hover:bg-white/20'
                  : 'text-white hover:bg-white/20'
                }`}
                aria-label={`Flash: ${torchMode}`}
                title={`Flash: ${torchMode} (tap to cycle)`}
              >
                {isTorchChecking ? <IconFocus /> : <TorchIcon mode={torchMode} />}
              </button>
            </>
          )}
          {shouldShowSwitchCameraControl && (
            <button
              onClick={switchCamera}
              disabled={!canSwitchCamera && availableCameraCount > 0}
              className="p-2 text-white hover:bg-white/20 rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
              aria-label={switchCameraLabel}
            >
              <IconSwitchCamera />
            </button>
          )}
          <button
            onClick={() => { void haptics.modalClose(); close(); }}
            className="p-2 text-white hover:bg-white/20 rounded-full transition-colors"
            aria-label="Close camera"
          >
            <IconClose />
          </button>
        </div>
      </div>

      {/* Camera view */}
      <div
        ref={videoContainerRef}
        className="relative bg-black overflow-hidden"
        style={{
          flex: '1 1 0%',
          minHeight: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        onClick={handleVideoTap}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {videoElement}
        {loadingOverlay}
        {errorOverlay}
        {focusRingElement}
        {zoomBadge}
        {guideOverlay}
        {lowLightBanner}
        {autoCaptureFloatingToggle}
        {autoZoomFloatingToggle}
        {afIndicator}
        {countdownOverlay}
        {batchStrip}
        {contextStrip}
      </div>

      {/* Zoom slider (horizontal) */}
      {zoomSupported && (
        <div className="bg-black/80 px-6 pt-1 pb-0" style={{ flexShrink: 0 }}>
          {zoomSlider}
        </div>
      )}

      {/* Floating Done button — above bottom controls for thumb reach */}
      {floatingDoneButton}

      {/* Capture mode grid or shortcuts + bottom controls */}
      {captureModeGridUI ? (
        <div className="bg-black/80" style={{ flexShrink: 0 }}>
          {captureModeGridUI}
        </div>
      ) : (
        <>
          {/* Contextual shortcuts */}
          {shortcutsBar && (
            <div className="bg-black/80" style={{ flexShrink: 0 }}>
              {shortcutsBar}
            </div>
          )}

          {/* Bottom controls bar */}
          <div
            className="bg-black/80 flex items-center justify-center"
            style={{
              flexShrink: 0,
              paddingTop: '14px',
              paddingBottom: 'max(32px, calc(env(safe-area-inset-bottom) + 28px))',
            }}
          >
            {captureButton || (
              <div className="flex items-center justify-center gap-6 px-4">
                {/* Flash toggle */}
                <div className="w-14 flex flex-col items-center justify-center flex-shrink-0 gap-1">
                  {shouldShowTorchControl ? (
                    <>
                      <CameraActionButton
                        onClick={handleTorchToggle}
                        active={torchMode !== 'off'}
                        activeClass={torchMode === 'auto' ? 'bg-amber-500/20 text-amber-300' : 'bg-yellow-500/30 text-yellow-300'}
                        label={`Flash: ${torchMode}`}
                      >
                        {isTorchChecking ? <IconFocus /> : <TorchIcon mode={torchMode} />}
                      </CameraActionButton>
                    </>
                  ) : (
                    <span />
                  )}
                </div>

                {/* Shutter */}
                {shutterButton}

                {/* Camera / focus actions */}
                <div className="w-14 flex items-center justify-center flex-shrink-0">
                  <div className="flex flex-col items-center gap-2">
                    {shouldShowSwitchCameraControl && (
                      <CameraActionButton
                        onClick={switchCamera}
                        disabled={!canSwitchCamera && availableCameraCount > 0}
                        label={switchCameraLabel}
                      >
                        <IconSwitchCamera />
                      </CameraActionButton>
                    )}
                    <CameraActionButton
                      onClick={() => tapToFocus(0.5, 0.5)}
                      active={!autofocusActive}
                      activeClass="bg-yellow-500/30 text-yellow-300"
                      label="Focus center"
                    >
                      <IconFocus />
                    </CameraActionButton>
                    {autoCaptureToggle}
                    {autoZoomToggle}
                  </div>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Focus ring keyframe animation */}
      <style>{focusRingKeyframes}</style>
      {reviewOverlay}
    </div>
  );

  return typeof document !== 'undefined'
    ? createPortal(portraitModal, document.body)
    : null;
}

export default CameraModal;
